import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Shield,
  ShieldCheck,
  TrendingUp,
  AlertTriangle,
  Sparkles,
  History,
  Globe,
  CreditCard,
  Menu,
  X,
  ChevronDown,
  LogOut,
  Plus,
  Check,
  Download,
  ExternalLink,
  AlertCircle,
  Copy,
  Loader2,
  Bell,
  ArrowRight,
  Mail,
  BookOpen,
  Circle,
  Eye,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useSEO } from '@/hooks/useSEO';
import DomainVerificationCard from '@/components/dashboard/DomainVerificationCard';
import { DashboardRecommendationsPanel } from '@/components/shared/RecommendationCards';
import IntervalToggle from '@/components/shared/IntervalToggle';
import { PLANS as PLAN_CATALOG } from '@/data/plans';

// Plan copy for the in-dashboard upgrade picker (trial → paid conversion).
// Derived from the shared catalog (single source of truth) — see
// src/data/plans.js. The "Acodia Watch " prefix is applied in the card JSX.
const UPGRADE_PLANS = Object.entries(PLAN_CATALOG).map(([id, plan]) => ({
  id,
  name: plan.name,
  monthly: `£${plan.price.monthly}`,
  annual: `£${plan.price.annual.toLocaleString('en-GB')}`,
  popular: Boolean(plan.mostPopular),
  valueProp: plan.valueProp,
  features: plan.features,
}));

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const NAV_ITEMS = [
  { id: 'domain-health',   label: 'Overview',        icon: Shield,         path: '/dashboard' },
  { id: 'domains',         label: 'Domain Health',   icon: Globe,          path: '/dashboard/domains' },
  { id: 'scan-history',    label: 'Scan History',    icon: History,        path: '/dashboard/scan-history' },
  { id: 'threat-timeline', label: 'Alerts',          icon: TrendingUp,     path: '/dashboard/alerts' },
  { id: 'cve-feed',        label: 'CVE Feed',        icon: AlertTriangle,  path: '/dashboard' },
  { id: 'ai-briefing',     label: 'AI Briefing',     icon: Sparkles,       path: '/dashboard' },
  { id: 'billing',         label: 'Billing',         icon: CreditCard,     path: '/dashboard/billing' },
];

// Route slug → activeSection id. Driven by the new /dashboard/<slug> routes.
const SECTION_FROM_PROP = {
  overview: 'domain-health',
  'domain-health': 'domain-health',
  domains: 'domains',
  'scan-history': 'scan-history',
  alerts: 'threat-timeline',
  'threat-timeline': 'threat-timeline',
  'cve-feed': 'cve-feed',
  'ai-briefing': 'ai-briefing',
  billing: 'billing',
};

export default function DashboardPage({ section: sectionProp = 'overview' }) {
  useSEO({
    title: 'Dashboard | Acodia — Grow. Defend. Be Found.',
    description: 'Your Acodia Watch Client Pulse Dashboard — scans, alerts, and domain health at a glance.',
    path: '/dashboard',
    robots: 'noindex,nofollow',
  });
  const location = useLocation();
  const { user, logout, authFetch } = useAuth();
  const initialSection = SECTION_FROM_PROP[sectionProp] || 'domain-health';
  const [activeSection, setActiveSection] = useState(initialSection);

  // Whenever the React Router route changes the section prop, sync local state.
  useEffect(() => {
    const mapped = SECTION_FROM_PROP[sectionProp] || 'domain-health';
    setActiveSection(mapped);
  }, [sectionProp]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [domains, setDomains] = useState([]);
  const [allDomains, setAllDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Hash-driven section selection. Reads the URL hash on mount and on every
  // hashchange so links like /dashboard#billing land the visitor on the
  // matching section and scroll it into view.
  useEffect(() => {
    const NAV_IDS = NAV_ITEMS.map((i) => i.id);
    const applyHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (!hash) return;
      if (NAV_IDS.includes(hash)) {
        setActiveSection(hash);
      }
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const wantsWelcome = params.get('welcome') === 'true';
      const alreadyShown = window.localStorage.getItem('acodia_welcome_shown') === '1';
      if (wantsWelcome && !alreadyShown) {
        setWelcomeOpen(true);
      }
    } catch {
      /* no-op */
    }
  }, []);

  // Show the 3-step onboarding modal the first time a visitor lands here
  // without any domains. `acodia_onboarded` is set on completion or skip so
  // subsequent visits never re-trigger it.
  useEffect(() => {
    let cancelled = false;
    try {
      const seen = window.localStorage.getItem('acodia_onboarded') === '1';
      if (seen) return undefined;
      if (allDomains.length > 0) return undefined;
      const t = setTimeout(() => {
        if (!cancelled) setOnboardingOpen(true);
      }, 1000);
      return () => {
        cancelled = true;
        clearTimeout(t);
      };
    } catch {
      return undefined;
    }
  }, [allDomains.length]);

  const completeOnboarding = () => {
    try { window.localStorage.setItem('acodia_onboarded', '1'); } catch { /* ignore */ }
    setOnboardingOpen(false);
    loadDashboardData();
  };

  const closeWelcome = () => {
    try { window.localStorage.setItem('acodia_welcome_shown', '1'); } catch { /* ignore */ }
    setWelcomeOpen(false);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('welcome');
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    } catch { /* ignore */ }
  };

  const goVerifyDomains = () => {
    setActiveSection('domains');
    closeWelcome();
    setTimeout(() => {
      const el = document.getElementById('domain-verification-grid');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const loadDashboardData = async () => {
    try {
      // Load domains
      const domRes = await authFetch(`${BACKEND_URL}/api/verify/domains`);
      if (domRes.ok) {
        const domData = await domRes.json();
        const verified = domData.domains.filter(d => d.verified);
        setDomains(verified);
        setAllDomains(domData.domains || []);
        if (verified.length > 0 && !selectedDomain) {
          setSelectedDomain(verified[0].domain);
        }
      }

      // Load subscription
      const subRes = await authFetch(`${BACKEND_URL}/api/billing/subscription`);
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    }
  };

  const getUserInitials = () => {
    if (!user?.email) return 'U';
    const parts = user.email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)' }}>
      {welcomeOpen && (
        <WelcomeModal
          domains={allDomains}
          onVerify={goVerifyDomains}
          onDismiss={closeWelcome}
        />
      )}
      {onboardingOpen && (
        <OnboardingModal
          user={user}
          authFetch={authFetch}
          onComplete={completeOnboarding}
        />
      )}
      {/* Sidebar */}
      <aside
        data-testid="dashboard-sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-40 transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{
          width: '240px',
          background: 'var(--l1)',
          borderRight: '1px solid var(--b1)',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-[68px] flex items-center px-6 border-b" style={{ borderColor: 'var(--b1)' }}>
            <a href="/" className="flex items-center gap-2">
              <div className="relative w-2 h-2">
                <div
                  className="absolute inset-0 rounded-full animate-pulse-dot"
                  style={{ background: 'var(--a)' }}
                />
              </div>
              <img
                src="/images/logo/logo-nav-64.png"
                alt="Acodia"
                style={{ height: '32px', width: 'auto', display: 'block' }}
              />
            </a>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-3">
            <div className="mb-4 px-3">
              <span 
                className="font-mono text-[0.62rem] uppercase tracking-[0.15em] font-medium"
                style={{ color: 'var(--te)' }}
              >
                Dashboard
              </span>
            </div>
            
            <div className="space-y-1">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                // Active = current route matches the item's path. Falls back
                // to in-page activeSection for items that share /dashboard
                // (CVE Feed, AI Briefing) and are surfaced via in-page state.
                const sharedPath = item.path === '/dashboard';
                const isActive = sharedPath
                  ? (location.pathname === '/dashboard' && activeSection === item.id)
                  : location.pathname === item.path;

                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    data-testid={`nav-${item.id}`}
                    onClick={() => {
                      setActiveSection(item.id);
                      setSidebarOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[3px] transition-all"
                    style={{
                      color: isActive ? 'var(--white)' : 'var(--ts)',
                      background: isActive ? 'rgba(139,92,246,0.12)' : 'transparent',
                      borderLeft: isActive ? '2px solid var(--a)' : '2px solid transparent',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                        e.currentTarget.style.color = 'var(--white)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'var(--ts)';
                      }
                    }}
                  >
                    <div className="relative flex items-center gap-3">
                      <div className="relative w-1.5 h-1.5">
                        <div
                          className="absolute inset-0 rounded-full animate-pulse-dot"
                          style={{ background: isActive ? 'var(--a)' : 'var(--ts)' }}
                        />
                      </div>
                      <Icon size={16} />
                    </div>
                    <span className="font-body text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Footer — support, docs, status */}
          <div
            data-testid="dashboard-sidebar-footer"
            className="border-t"
            style={{ borderColor: 'var(--b1)', padding: '0.75rem 1rem 1rem' }}
          >
            <a
              href="mailto:support@acodia.co.uk"
              data-testid="sidebar-help"
              className="flex items-center"
              style={{
                gap: '0.5rem', padding: '0.4rem 0.5rem', borderRadius: 3,
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem',
                color: 'var(--ts)', textDecoration: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--white)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ts)'; e.currentTarget.style.background = 'transparent'; }}
            >
              <Mail size={13} /> Get help
            </a>
            <a
              href="/blog"
              data-testid="sidebar-docs"
              className="flex items-center"
              style={{
                gap: '0.5rem', padding: '0.4rem 0.5rem', borderRadius: 3,
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem',
                color: 'var(--ts)', textDecoration: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--white)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ts)'; e.currentTarget.style.background = 'transparent'; }}
            >
              <BookOpen size={13} /> Documentation
            </a>
            <a
              href="#"
              data-testid="sidebar-status"
              className="flex items-center"
              style={{
                gap: '0.5rem', padding: '0.4rem 0.5rem', borderRadius: 3,
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem',
                color: 'var(--ts)', textDecoration: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--white)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ts)'; e.currentTarget.style.background = 'transparent'; }}
            >
              <Circle
                size={9}
                fill="var(--ok)"
                style={{ color: 'var(--ok)', animation: 'pulseDot 1.4s ease-in-out infinite' }}
              />
              All systems operational
            </a>
            <p
              className="font-mono"
              style={{ fontSize: '0.62rem', color: 'var(--te)', marginTop: '0.75rem' }}
            >
              Acodia Platform v1.0.0
            </p>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header
          data-testid="dashboard-topbar"
          className="sticky top-0 z-20 flex items-center justify-between gap-4 px-6 h-[68px]"
          style={{
            background: 'var(--l1)',
            borderBottom: '1px solid var(--b1)',
          }}
        >
          {/* Left side */}
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{ color: 'var(--white)' }}
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Logo mark (mobile) */}
            <a href="/" className="lg:hidden flex items-center gap-2">
              <div className="relative w-2 h-2">
                <div
                  className="absolute inset-0 rounded-full animate-pulse-dot"
                  style={{ background: 'var(--a)' }}
                />
              </div>
              <img
                src="/images/logo/logo-nav-64.png"
                alt="Acodia"
                style={{ height: '28px', width: 'auto', display: 'block' }}
              />
            </a>

            {/* Section title */}
            <h1 className="font-display text-base font-bold hidden lg:block" style={{ color: 'var(--white)' }}>
              {NAV_ITEMS.find(item => item.id === activeSection)?.label}
            </h1>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Domain selector (hidden on mobile) */}
            {domains.length > 0 && (
              <div className="hidden md:block relative">
                <select
                  data-testid="domain-selector"
                  value={selectedDomain || ''}
                  onChange={(e) => setSelectedDomain(e.target.value)}
                  className="appearance-none font-mono text-xs px-3 py-1.5 pr-8 rounded-[3px] cursor-pointer"
                  style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid var(--b1)',
                    color: 'var(--white)',
                  }}
                >
                  {domains.map((d) => (
                    <option key={d.id} value={d.domain}>
                      {d.domain}
                    </option>
                  ))}
                </select>
                <ChevronDown 
                  size={12} 
                  className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: 'var(--ts)' }}
                />
              </div>
            )}

            {/* Plan badge — reflects trial / active / expired */}
            <PlanBadge subscription={subscription?.subscription} />


            {/* User avatar */}
            <div className="relative group">
              <button
                data-testid="user-avatar"
                className="flex items-center justify-center w-8 h-8 rounded-full font-body text-xs font-bold"
                style={{
                  background: 'var(--a)',
                  color: '#fff',
                }}
              >
                {getUserInitials()}
              </button>

              {/* Dropdown menu */}
              <div className="absolute right-0 top-full mt-2 w-48 py-2 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all"
                style={{
                  background: 'var(--l1)',
                  border: '1px solid var(--b1)',
                }}
              >
                <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--b1)' }}>
                  <p className="font-body text-xs" style={{ color: 'var(--white)' }}>
                    {user?.email}
                  </p>
                  <p className="font-mono text-[0.62rem]" style={{ color: 'var(--ts)' }}>
                    {(() => {
                      const s = subscription?.status;
                      if (s === 'trial') return 'Trial';
                      if (s === 'active') return 'Active';
                      if (s === 'cancelling') return 'Cancelling';
                      if (s === 'expired') return 'Expired';
                      return 'Inactive';
                    })()}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2 font-body text-xs hover:bg-white/5"
                  style={{ color: 'var(--ts)' }}
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main
          data-testid="dashboard-content"
          className="flex-1 overflow-y-auto p-7"
          style={{ background: 'var(--bg)' }}
        >
          <TrialBanner
            subscription={subscription?.subscription}
            onAddCard={() => setActiveSection('billing')}
          />
          <UnverifiedDomainsBanner
            domains={allDomains}
            authFetch={authFetch}
            onVerified={loadDashboardData}
          />
          {activeSection === 'domain-health' && (
            <div id="domain-health"><DomainHealthSection /></div>
          )}
          {activeSection === 'threat-timeline' && (
            <div id="alerts"><ThreatTimelineSection /></div>
          )}
          {activeSection === 'cve-feed' && (
            <div id="cve-feed"><CVEFeedSection /></div>
          )}
          {activeSection === 'ai-briefing' && (
            <div id="ai-briefing"><AIBriefingSection /></div>
          )}
          {activeSection === 'scan-history' && (
            <div id="scan-history"><ScanHistorySection /></div>
          )}
          {activeSection === 'domains' && (
            <div id="domains"><DomainsSection /></div>
          )}
          {activeSection === 'billing' && (
            <div id="billing">
              <BillingSection
                subscription={subscription?.subscription}
                onCancelled={loadDashboardData}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ─────────── Trial helpers ───────────
function daysRemaining(trialEndsAt) {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt).getTime();
  if (Number.isNaN(end)) return null;
  const ms = end - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

function PlanBadge({ subscription }) {
  const status = subscription?.status;
  if (status === 'trial') {
    const days = daysRemaining(subscription?.trial_ends_at);
    const urgent = typeof days === 'number' && days <= 2;
    const color = urgent ? 'var(--warn)' : 'var(--ok)';
    const bg = urgent ? 'rgba(251,191,36,0.1)' : 'rgba(74,222,128,0.1)';
    const border = urgent ? 'rgba(251,191,36,0.25)' : 'rgba(74,222,128,0.25)';
    return (
      <div
        data-testid="plan-badge"
        className="flex items-center gap-2 px-3 py-1.5 rounded-[3px]"
        style={{ background: bg, border: `1px solid ${border}`, color }}
      >
        <div className="relative w-1.5 h-1.5">
          <div className="absolute inset-0 rounded-full animate-pulse-dot" style={{ background: color }} />
        </div>
        <span className="font-mono text-[0.62rem] font-medium uppercase tracking-wider">
          Trial · {Math.max(days ?? 0, 0)}d left
        </span>
      </div>
    );
  }
  if (status === 'expired') {
    return (
      <div
        data-testid="plan-badge"
        className="flex items-center gap-2 px-3 py-1.5 rounded-[3px]"
        style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', color: 'var(--danger)' }}
      >
        <span className="font-mono text-[0.62rem] font-medium uppercase tracking-wider">Trial expired</span>
      </div>
    );
  }
  if (status === 'cancelling') {
    return (
      <div
        data-testid="plan-badge"
        className="flex items-center gap-2 px-3 py-1.5 rounded-[3px]"
        style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', color: 'var(--warn)' }}
      >
        <span className="font-mono text-[0.62rem] font-medium uppercase tracking-wider">Cancelling</span>
      </div>
    );
  }
  return (
    <div
      data-testid="plan-badge"
      className="flex items-center gap-2 px-3 py-1.5 rounded-[3px]"
      style={{ background: 'var(--a-glow)', border: '1px solid var(--b2)', color: 'var(--a)' }}
    >
      <div className="relative w-1.5 h-1.5">
        <div className="absolute inset-0 rounded-full animate-pulse-dot" style={{ background: 'var(--a)' }} />
      </div>
      <span className="font-mono text-[0.62rem] font-medium uppercase tracking-wider">Acodia Watch</span>
    </div>
  );
}

function TrialBanner({ subscription, onAddCard }) {
  const status = subscription?.status;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const key = 'acodia_trial_banner_dismissed';
      const val = window.sessionStorage.getItem(key);
      if (val === '1') setDismissed(true);
    } catch { /* ignore */ }
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { window.sessionStorage.setItem('acodia_trial_banner_dismissed', '1'); } catch { /* ignore */ }
  };

  if (!subscription) return null;
  if (!['trial', 'expired'].includes(status)) return null;
  if (dismissed && status !== 'expired') return null;

  const days = daysRemaining(subscription.trial_ends_at);
  const isExpired = status === 'expired' || (typeof days === 'number' && days <= 0);
  const urgent = !isExpired && typeof days === 'number' && days <= 2;

  const palette = isExpired
    ? { bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.35)', color: 'var(--danger)', btnBg: 'var(--danger)' }
    : urgent
    ? { bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.35)', color: 'var(--warn)', btnBg: 'var(--warn)' }
    : { bg: 'rgba(74,222,128,0.08)', border: 'rgba(74,222,128,0.35)', color: 'var(--ok)', btnBg: 'var(--a)' };

  const message = isExpired
    ? 'Your trial has ended — add your card to reactivate monitoring'
    : urgent
    ? `Your trial ends in ${Math.max(days, 0)} day${days === 1 ? '' : 's'} — add your card now to avoid interruption`
    : `Your free trial ends in ${days} days — card required to continue`;

  return (
    <div
      data-testid="trial-banner"
      className="mb-6 flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-[6px]"
      style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
    >
      <div className="flex items-center gap-3">
        <div className="relative w-2 h-2">
          <div className="absolute inset-0 rounded-full animate-pulse-dot" style={{ background: palette.color }} />
        </div>
        <span className="font-body text-sm" style={{ color: palette.color }}>
          {message}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          data-testid="trial-add-card"
          onClick={onAddCard}
          className="font-body font-medium text-xs h-8 px-3 rounded-[3px]"
          style={{ background: palette.btnBg, color: '#fff' }}
        >
          Add Payment Details
        </Button>
        {!isExpired && (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="w-8 h-8 flex items-center justify-center rounded-[3px]"
            style={{ color: palette.color }}
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}


function UnverifiedDomainsBanner({ domains = [], authFetch, onVerified }) {
  const unverified = (domains || []).filter((d) => !d.verified);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [checking, setChecking] = useState(false);
  const [resultMsg, setResultMsg] = useState(null);
  const [resultOk, setResultOk] = useState(false);
  const [copied, setCopied] = useState(false);

  if (unverified.length === 0) return null;

  const first = unverified[0];

  const openModal = () => {
    setTarget(first);
    setResultMsg(null);
    setResultOk(false);
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setTarget(null);
    setResultMsg(null);
    setChecking(false);
  };

  const txtHost = target ? `_acodia-verify.${target.domain}` : '';
  const txtValue = target?.verification_token || '';

  const copyValue = async () => {
    try {
      await navigator.clipboard.writeText(txtValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const checkNow = async () => {
    if (!target) return;
    setChecking(true);
    setResultMsg(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/domains/${encodeURIComponent(target.id)}/verify-dns`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      setResultOk(!!data.verified);
      setResultMsg(data.message || (data.verified ? 'Verified.' : 'Not yet — try again shortly.'));
      if (data.verified && typeof onVerified === 'function') {
        onVerified();
        setTimeout(closeModal, 1200);
      }
    } catch (err) {
      setResultOk(false);
      setResultMsg(err?.message || 'Network error. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <div
        data-testid="unverified-domains-banner"
        style={{
          background: 'rgba(217,119,6,0.1)',
          border: '1px solid rgba(217,119,6,0.3)',
          borderRadius: 10,
          padding: '1rem 1.5rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <AlertTriangle size={22} style={{ color: 'var(--warn)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            className="font-display"
            style={{ color: 'var(--white)', fontWeight: 600, fontSize: '0.95rem' }}
          >
            Domain verification required
          </p>
          <p
            className="font-body"
            style={{ color: 'var(--ts)', fontSize: '0.85rem' }}
          >
            Verify {first.domain} to activate your Acodia Watch monitoring.
            {unverified.length > 1 ? ` (${unverified.length - 1} more pending.)` : ''}
          </p>
        </div>
        <Link
          to={`/dashboard/domains/verify/${first.id}`}
          data-testid="unverified-banner-verify"
          className="font-body font-medium text-sm px-4 h-10 rounded-[3px] inline-flex items-center gap-1.5"
          style={{ background: 'var(--a)', color: '#fff', flexShrink: 0, textDecoration: 'none' }}
        >
          Verify Now →
        </Link>
      </div>

      {open && target && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeModal}
          style={{
            position: 'fixed', inset: 0, zIndex: 60,
            background: 'rgba(10,8,18,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--l1)',
              border: '1px solid var(--b1)',
              borderRadius: 12,
              padding: '1.75rem',
              maxWidth: 560,
              width: '100%',
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-display text-lg font-bold" style={{ color: 'var(--white)' }}>
                Verify your domain
              </h3>
              <button
                type="button"
                onClick={closeModal}
                style={{ color: 'var(--ts)' }}
              >
                <X size={18} />
              </button>
            </div>

            <p className="font-body text-sm mb-4" style={{ color: 'rgba(240,237,248,0.78)' }}>
              Add this TXT record to your domain&apos;s DNS settings to verify ownership:
            </p>

            <div
              className="rounded-[6px] p-3 mb-3"
              style={{ background: 'var(--bg)', border: '1px solid var(--b1)' }}
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-mono text-xs" style={{ color: 'var(--ts)' }}>Type</span>
                <code className="font-mono text-xs" style={{ color: 'var(--white)' }}>TXT</code>
              </div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-mono text-xs" style={{ color: 'var(--ts)' }}>Host</span>
                <code className="font-mono text-xs break-all" style={{ color: 'var(--white)' }}>{txtHost}</code>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-xs" style={{ color: 'var(--ts)' }}>Value</span>
                <div className="inline-flex items-center gap-2">
                  <code className="font-mono text-xs break-all" style={{ color: 'var(--ok)' }}>{txtValue}</code>
                  <button
                    type="button"
                    onClick={copyValue}
                    className="inline-flex items-center gap-1 font-body text-xs px-2 py-1 rounded-[3px]"
                    style={{
                      background: copied ? 'rgba(74,222,128,0.1)' : 'rgba(139,92,246,0.08)',
                      border: `1px solid ${copied ? 'rgba(74,222,128,0.25)' : 'var(--b2)'}`,
                      color: copied ? 'var(--ok)' : 'var(--a-bright)',
                    }}
                  >
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>

            <p className="font-body text-xs mb-5" style={{ color: 'var(--ts)' }}>
              Log in to your domain registrar (e.g. Hostinger, GoDaddy) and add this TXT record. DNS changes can take up to 24 hours to propagate.
            </p>

            {resultMsg && (
              <div
                className="rounded-[6px] p-3 mb-4"
                style={{
                  background: resultOk ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.08)',
                  border: `1px solid ${resultOk ? 'rgba(74,222,128,0.25)' : 'rgba(248,113,113,0.25)'}`,
                  color: resultOk ? 'var(--ok)' : 'var(--danger)',
                }}
              >
                <p className="font-body text-sm">{resultMsg}</p>
              </div>
            )}

            <button
              type="button"
              data-testid="unverified-modal-check"
              onClick={checkNow}
              disabled={checking}
              className="w-full btn-shimmer font-body font-medium text-sm h-11 rounded-[3px]"
              style={{ background: 'var(--a)', color: '#fff', opacity: checking ? 0.7 : 1 }}
            >
              {checking ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader2 size={14} className="dom-spin" /> Checking…
                </span>
              ) : "I've added the DNS record — Check Now"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}


// Placeholder section components
function SectionPlaceholder({ title, description, icon: Icon }) {
  return (
    <div>
      {/* Section header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Icon size={24} style={{ color: 'var(--a)' }} />
          <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
            {title}
          </h2>
        </div>
        <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
          {description}
        </p>
      </div>

      {/* Loading skeleton card */}
      <div
        className="rounded-lg p-6"
        style={{
          background: 'var(--l1)',
          border: '1px solid var(--b1)',
        }}
      >
        <div className="space-y-4 animate-pulse">
          <div className="h-4 rounded" style={{ background: 'var(--l3)', width: '60%' }} />
          <div className="h-4 rounded" style={{ background: 'var(--l3)', width: '80%' }} />
          <div className="h-4 rounded" style={{ background: 'var(--l3)', width: '40%' }} />
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="h-24 rounded" style={{ background: 'var(--l3)' }} />
            <div className="h-24 rounded" style={{ background: 'var(--l3)' }} />
            <div className="h-24 rounded" style={{ background: 'var(--l3)' }} />
          </div>
        </div>
        <p className="font-mono text-xs mt-6 text-center" style={{ color: 'var(--te)' }}>
          Content coming in Step 3
        </p>
      </div>
    </div>
  );
}

function DomainHealthSection() {
  const { authFetch } = useAuth();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanningDomain, setScanningDomain] = useState(null);
  const [scanProgress, setScanProgress] = useState([]);
  const [showTerminal, setShowTerminal] = useState(false);
  const [rateLimitedDomains, setRateLimitedDomains] = useState({});  // {domain: retryAfterSeconds}
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  useEffect(() => {
    loadDomainScans();
  }, []);

  // Rate limit countdown timers
  useEffect(() => {
    const interval = setInterval(() => {
      setRateLimitedDomains(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(domain => {
          updated[domain] = Math.max(0, updated[domain] - 1);
          if (updated[domain] === 0) {
            delete updated[domain];
          }
        });
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const loadDomainScans = async () => {
    try {
      const domRes = await authFetch(`${BACKEND_URL}/api/verify/domains`);

      if (!domRes.ok) {
        console.error('Failed to load domains');
        setLoading(false);
        return;
      }

      const domData = await domRes.json();
      // PostgreSQL may surface booleans as true/'true'/1 depending on path —
      // accept any truthy-equivalent representation.
      const isVerified = (d) =>
        d?.verified === true || d?.verified === 'true' || d?.verified === 1;
      const verifiedDomains = (domData.domains || []).filter(isVerified);
      
      // For each domain, fetch latest scan result
      const scanPromises = verifiedDomains.map(async (domain) => {
        // Fetch latest scan from scan_results collection
        // For now, use mock data since we need to create a query endpoint
        return {
          id: domain.id,
          domain: domain.domain,
          overallGrade: 'B',
          tlsGrade: 'A',
          headerScore: 78,
          cveCount: 2,
          lastScanned: domain.created_at
        };
      });
      
      const scanResults = await Promise.all(scanPromises);
      setScans(scanResults);
      setLoading(false);
    } catch (error) {
      console.error('Error loading domain scans:', error);
      setLoading(false);
    }
  };

  const handleScanNow = async (domain) => {
    // Check if already scanning
    if (scanningDomain) return;
    
    // Check if rate limited
    if (rateLimitedDomains[domain]) return;
    
    setScanningDomain(domain);
    setShowTerminal(true);
    setScanProgress([{
      step: `Initiating Acodia Watch scan for ${domain}...`,
      completed: true,
      timestamp: new Date().toISOString()
    }]);
    
    try {
      const initRes = await authFetch(`${BACKEND_URL}/api/scan/active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain })
      });
      
      if (!initRes.ok) {
        const errorData = await initRes.json();
        
        // Handle rate limiting
        if (initRes.status === 429 && errorData.retry_after_seconds) {
          setScanProgress(prev => [...prev, {
            step: `Rate limit exceeded. Next scan available in ${Math.floor(errorData.retry_after_seconds / 60)} minutes.`,
            completed: false,
            error: true,
            timestamp: new Date().toISOString()
          }]);
          
          setRateLimitedDomains(prev => ({
            ...prev,
            [domain]: errorData.retry_after_seconds
          }));
          
          setTimeout(() => {
            setShowTerminal(false);
            setScanningDomain(null);
          }, 3000);
          
          return;
        }
        
        throw new Error(errorData.detail || 'Scan initiation failed');
      }
      
      const initData = await initRes.json();
      const scanId = initData.scan_id;

      // Poll for status
      await pollScanStatus(scanId, domain);

    } catch (error) {
      console.error('Scan failed:', error);
      setScanProgress(prev => [...prev, {
        step: `✗ Scan failed — ${error.message}. Please try again.`,
        completed: false,
        error: true,
        timestamp: new Date().toISOString()
      }]);
      
      setToastMessage('Scan failed — please try again');
      setToastVariant('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      
      setTimeout(() => {
        setShowTerminal(false);
        setScanningDomain(null);
      }, 3000);
    }
  };

  const pollScanStatus = async (scanId, domain) => {
    const maxAttempts = 200;  // 10 minutes (200 * 3 seconds)
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setScanProgress(prev => [...prev, {
          step: '✗ Scan timeout — Please try again.',
          completed: false,
          error: true,
          timestamp: new Date().toISOString()
        }]);

        setTimeout(() => {
          setShowTerminal(false);
          setScanningDomain(null);
        }, 3000);

        return;
      }

      try {
        const statusRes = await authFetch(`${BACKEND_URL}/api/scan/status/${scanId}`);
        
        if (!statusRes.ok) {
          throw new Error('Status check failed');
        }
        
        const statusData = await statusRes.json();
        
        // Update progress
        setScanProgress(statusData.progress);
        
        if (statusData.status === 'complete') {
          // Success! Update domain card with new data
          setTimeout(() => {
            setShowTerminal(false);
            setScanningDomain(null);
            
            // Update scan in list
            setScans(prev => prev.map(s => 
              s.domain === domain 
                ? {
                    ...s,
                    overallGrade: statusData.result.risk_score?.grade || 'N/A',
                    tlsGrade: statusData.result.tls?.grade || 'N/A',
                    headerScore: statusData.result.headers?.score || 0,
                    cveCount: statusData.result.cves?.count || 0,
                    lastScanned: new Date().toISOString()
                  }
                : s
            ));
            
            // Show toast
            const grade = statusData.result.risk_score?.grade || 'N/A';
            const hasCritical = statusData.result.cves?.severity_breakdown?.critical > 0 ||
                               statusData.result.cves?.severity_breakdown?.high > 0;
            
            setToastMessage(`Scan complete — ${domain} risk score: ${grade}`);
            setToastVariant(hasCritical ? 'warning' : 'success');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
            
            // Set rate limit (1 hour)
            setRateLimitedDomains(prev => ({
              ...prev,
              [domain]: 3600
            }));
          }, 1500);
          
          return;
        }
        
        if (statusData.status === 'failed') {
          setScanProgress(prev => [...prev, {
            step: `✗ ${statusData.error || 'Scan failed'}`,
            completed: false,
            error: true,
            timestamp: new Date().toISOString()
          }]);
          
          setTimeout(() => {
            setShowTerminal(false);
            setScanningDomain(null);
          }, 3000);
          
          return;
        }
        
        // Continue polling
        attempts++;
        setTimeout(poll, 3000);  // Poll every 3 seconds
        
      } catch (error) {
        console.error('Polling error:', error);
        setTimeout(poll, 3000);
      }
    };
    
    poll();
  };

  const getGradeColor = (grade) => {
    if (grade === 'A' || grade === 'A+') return 'var(--ok)';
    if (grade === 'B') return 'var(--warn)';
    return 'var(--danger)';
  };

  const getStatusDotColor = (lastScanned) => {
    const daysSince = Math.floor((Date.now() - new Date(lastScanned).getTime()) / (1000 * 60 * 60 * 24));
    if (daysSince <= 7) return 'var(--ok)';
    if (daysSince <= 14) return 'var(--warn)';
    return 'var(--danger)';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--a)' }}></div>
      </div>
    );
  }

  if (scans.length === 0) {
    return (
      <div>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              Domain Health Overview
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Real-time security status for all verified domains
          </p>
        </div>

        <div
          className="rounded-lg p-12 flex flex-col items-center justify-center text-center"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <Shield size={64} style={{ color: 'var(--a)', marginBottom: '24px', opacity: 0.3 }} />
          <h3 className="font-display text-lg font-bold mb-2" style={{ color: 'var(--white)' }}>
            No verified domains yet
          </h3>
          <p className="font-body text-sm mb-6" style={{ color: 'var(--ts)', maxWidth: '400px' }}>
            Add and verify your first domain to start Acodia Watch monitoring.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Section header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield size={24} style={{ color: 'var(--a)' }} />
          <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
            Domain Health Overview
          </h2>
        </div>
        <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
          Real-time security status for all verified domains
        </p>
      </div>

      {/* Domain cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {scans.map((scan) => {
          const isScanning = scanningDomain === scan.domain;
          const isRateLimited = rateLimitedDomains[scan.domain] > 0;
          
          return (
            <div key={scan.id}>
              <div
                data-testid={`domain-health-card-${scan.domain}`}
                className="rounded-lg p-6"
                style={{
                  background: 'var(--l1)',
                  border: '1px solid var(--b1)',
                }}
              >
                {/* Domain header with status dot */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="relative w-2 h-2">
                      <div
                        className="absolute inset-0 rounded-full animate-pulse-dot"
                        style={{ background: getStatusDotColor(scan.lastScanned) }}
                      />
                    </div>
                    <h3 className="font-mono text-sm font-medium" style={{ color: 'var(--white)' }}>
                      {scan.domain}
                    </h3>
                  </div>
                </div>

                {/* Overall grade - large display */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span
                      className="font-display text-5xl font-extrabold"
                      style={{ color: getGradeColor(scan.overallGrade) }}
                    >
                      {scan.overallGrade}
                    </span>
                    <span className="font-body text-sm" style={{ color: 'var(--ts)' }}>
                      Overall Grade
                    </span>
                  </div>
                </div>

                {/* Metrics grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
                      TLS Grade
                    </p>
                    <p
                      className="font-mono text-lg font-bold"
                      style={{ color: getGradeColor(scan.tlsGrade) }}
                    >
                      {scan.tlsGrade}
                    </p>
                  </div>
                  <div>
                    <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
                      Header Score
                    </p>
                    <p
                      className="font-mono text-lg font-bold"
                      style={{ color: scan.headerScore >= 90 ? 'var(--ok)' : scan.headerScore >= 70 ? 'var(--warn)' : 'var(--danger)' }}
                    >
                      {scan.headerScore}
                    </p>
                  </div>
                  <div>
                    <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
                      CVE Count
                    </p>
                    <p
                      className="font-mono text-lg font-bold"
                      style={{ color: scan.cveCount === 0 ? 'var(--ok)' : scan.cveCount <= 5 ? 'var(--warn)' : 'var(--danger)' }}
                    >
                      {scan.cveCount}
                    </p>
                  </div>
                  <div>
                    <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
                      Last Scanned
                    </p>
                    <p className="font-mono text-xs" style={{ color: 'var(--ts)' }}>
                      {formatDate(scan.lastScanned)}
                    </p>
                  </div>
                </div>

                {/* Scan Now button */}
                <Button
                  onClick={() => handleScanNow(scan.domain)}
                  disabled={isScanning || isRateLimited}
                  className="w-full font-body text-sm px-4 py-2 rounded-[3px] flex items-center justify-center gap-2"
                  style={{
                    background: isScanning || isRateLimited ? 'transparent' : 'var(--a)',
                    border: isScanning || isRateLimited ? '1px solid var(--b1)' : 'none',
                    color: isScanning || isRateLimited ? 'var(--te)' : '#fff',
                    opacity: isScanning || isRateLimited ? 0.6 : 1,
                    cursor: isScanning || isRateLimited ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isScanning ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: 'var(--a)' }}></div>
                      Scanning...
                    </>
                  ) : isRateLimited ? (
                    <>Next scan in {formatCountdown(rateLimitedDomains[scan.domain])}</>
                  ) : (
                    'Scan Now'
                  )}
                </Button>
              </div>

              {/* Terminal progress animation - inline below card */}
              {isScanning && showTerminal && (
                <div
                  className="mt-4 rounded-lg p-4 overflow-y-auto max-h-64 terminal-scanline"
                  style={{
                    background: 'rgba(0,0,0,0.6)',
                    border: '1px solid rgba(139,92,246,0.22)',
                    position: 'relative',
                  }}
                >
                  {scanProgress.map((step, idx) => (
                    <div
                      key={idx}
                      className="font-mono text-[0.72rem] mb-1"
                      style={{
                        color: step.error 
                          ? 'var(--danger)' 
                          : step.completed 
                            ? 'var(--ok)' 
                            : 'rgba(167,139,250,0.85)',
                      }}
                    >
                      {step.error ? '✗' : step.completed ? '✓' : '→'} {step.step}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Toast notification */}
      {showToast && (
        <div
          className="fixed bottom-6 right-6 px-4 py-3 rounded-md shadow-lg animate-fade-in z-50"
          style={{
            background: 'var(--l3)',
            border: `1px solid ${toastVariant === 'warning' ? 'var(--warn)' : 'var(--b1)'}`,
          }}
        >
          <p
            className="font-body text-[0.78rem]"
            style={{
              color: toastVariant === 'warning' ? 'var(--warn)' : 'var(--white)',
            }}
          >
            {toastMessage}
          </p>
        </div>
      )}
    </div>
  );
}

function ThreatTimelineSection() {
  const [hasData, setHasData] = useState(true);

  // Generate 13 weeks of mock data
  const generateMockData = () => {
    const data = [];
    const now = new Date();
    
    // Define the pattern: moderate → spike → declining
    const patterns = [
      // Weeks 1-4: moderate activity
      { critical: [2, 3, 4, 3], high: [4, 5, 6, 5], medium: [5, 6, 7, 8] },
      // Weeks 5-8: spike in activity
      { critical: [5, 7, 8, 6], high: [7, 9, 10, 8], medium: [9, 11, 12, 10] },
      // Weeks 9-13: declining trend
      { critical: [3, 2, 2, 1, 1], high: [5, 4, 3, 2, 2], medium: [6, 5, 4, 3, 3] },
    ];
    
    const allCritical = [...patterns[0].critical, ...patterns[1].critical, ...patterns[2].critical];
    const allHigh = [...patterns[0].high, ...patterns[1].high, ...patterns[2].high];
    const allMedium = [...patterns[0].medium, ...patterns[1].medium, ...patterns[2].medium];
    
    for (let i = 12; i >= 0; i--) {
      const weekDate = new Date(now);
      weekDate.setDate(weekDate.getDate() - (i * 7));
      
      data.push({
        week: weekDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        fullDate: weekDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        critical: allCritical[12 - i],
        high: allHigh[12 - i],
        medium: allMedium[12 - i],
      });
    }
    
    return data;
  };

  const data = generateMockData();

  // Calculate summary stats
  const totalFindings = data.reduce((sum, week) => sum + week.critical + week.high + week.medium, 0);
  const criticalCount = data.reduce((sum, week) => sum + week.critical, 0);
  const highCount = data.reduce((sum, week) => sum + week.high, 0);
  
  // Calculate trend (last 4 weeks vs previous 4 weeks)
  const recentSum = data.slice(-4).reduce((sum, week) => sum + week.critical + week.high + week.medium, 0);
  const previousSum = data.slice(-8, -4).reduce((sum, week) => sum + week.critical + week.high + week.medium, 0);
  const trendPercent = previousSum > 0 ? Math.round(((recentSum - previousSum) / previousSum) * 100) : 0;
  const trendUp = trendPercent > 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="p-3 rounded-md"
          style={{
            background: 'var(--l3)',
            border: '1px solid var(--b2)',
          }}
        >
          <p className="font-mono text-xs mb-2" style={{ color: 'var(--ts)' }}>
            {payload[0]?.payload?.fullDate}
          </p>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ background: 'var(--danger)' }} />
              <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>
                Critical: {payload[0]?.value || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ background: 'var(--warn)' }} />
              <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>
                High: {payload[1]?.value || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ background: 'var(--info)' }} />
              <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>
                Medium: {payload[2]?.value || 0}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!hasData) {
    return (
      <div>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              Threat Timeline
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Finding severity over the past 90 days
          </p>
        </div>

        {/* Empty state — no alerts. Bell + green tick to signal "all clear". */}
        <div
          data-testid="alerts-empty-state"
          className="rounded-lg p-12 flex flex-col items-center justify-center text-center"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <div style={{ position: 'relative', marginBottom: 18 }}>
            <Bell size={56} style={{ color: 'var(--a)', opacity: 0.5 }} />
            <span
              style={{
                position: 'absolute', right: -4, bottom: -4,
                width: 22, height: 22, borderRadius: '50%',
                background: 'var(--ok)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid var(--bg)',
              }}
            >
              <Check size={12} style={{ color: '#fff' }} />
            </span>
          </div>
          <h3 className="font-display text-lg font-bold mb-2" style={{ color: 'var(--white)' }}>
            No alerts — you&apos;re all clear
          </h3>
          <p className="font-body text-sm" style={{ color: 'var(--ts)', maxWidth: '420px' }}>
            Acodia Watch is monitoring your website. We&apos;ll notify you immediately if anything changes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Section header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              Threat Timeline
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Finding severity over the past 90 days
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-[0.62rem]" style={{ color: 'var(--te)' }}>
            Last updated: {new Date().toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: 'short', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
      </div>

      {/* Summary stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div
          className="p-4 rounded-lg"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
            Total Findings
          </p>
          <p className="font-display text-2xl font-bold" style={{ color: 'var(--white)' }}>
            {totalFindings}
          </p>
        </div>

        <div
          className="p-4 rounded-lg"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
            Critical
          </p>
          <p className="font-display text-2xl font-bold" style={{ color: 'var(--danger)' }}>
            {criticalCount}
          </p>
        </div>

        <div
          className="p-4 rounded-lg"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
            High
          </p>
          <p className="font-display text-2xl font-bold" style={{ color: 'var(--warn)' }}>
            {highCount}
          </p>
        </div>

        <div
          className="p-4 rounded-lg"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
            Trend
          </p>
          <div className="flex items-center gap-2">
            <span className="font-display text-2xl font-bold" style={{ color: trendUp ? 'var(--danger)' : 'var(--ok)' }}>
              {trendUp ? '↑' : '↓'} {Math.abs(trendPercent)}%
            </span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div
        className="rounded-lg p-6"
        style={{
          background: 'var(--l1)',
          border: '1px solid var(--b1)',
        }}
      >
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="criticalGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--danger)" stopOpacity={0.15} />
                <stop offset="95%" stopColor="var(--danger)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="highGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--warn)" stopOpacity={0.15} />
                <stop offset="95%" stopColor="var(--warn)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="mediumGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--info)" stopOpacity={0.12} />
                <stop offset="95%" stopColor="var(--info)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="var(--b1)" 
              horizontal={true} 
              vertical={false} 
            />
            <XAxis 
              dataKey="week" 
              tick={{ 
                fill: 'var(--ts)', 
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.62rem'
              }}
              stroke="var(--b1)"
            />
            <YAxis 
              tick={{ 
                fill: 'var(--ts)', 
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.62rem'
              }}
              stroke="var(--b1)"
              label={{ 
                value: 'Findings', 
                angle: -90, 
                position: 'insideLeft',
                style: {
                  fill: 'var(--ts)',
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.75rem'
                }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{
                paddingTop: '20px',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '0.72rem',
                color: 'var(--ts)'
              }}
              iconType="circle"
            />
            <Area 
              type="monotone" 
              dataKey="critical" 
              stroke="var(--danger)" 
              strokeWidth={1.5}
              fill="url(#criticalGradient)" 
              name="Critical"
              dot={false}
            />
            <Area 
              type="monotone" 
              dataKey="high" 
              stroke="var(--warn)" 
              strokeWidth={1.5}
              fill="url(#highGradient)" 
              name="High"
              dot={false}
            />
            <Area 
              type="monotone" 
              dataKey="medium" 
              stroke="var(--info)" 
              strokeWidth={1.5}
              fill="url(#mediumGradient)" 
              name="Medium"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CVEFeedSection() {
  const [cves, setCves] = useState([]);
  const [sortBy, setSortBy] = useState('severity');
  const [sortDesc, setSortDesc] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadMockData();
  }, []);

  const loadMockData = () => {
    const mockCVEs = [
      {
        id: 'CVE-2024-23917',
        severity: 'Critical',
        component: 'Apache HTTP Server 2.4.51',
        detected: '2026-01-15',
        status: 'Open',
      },
      {
        id: 'CVE-2024-38856',
        severity: 'High',
        component: 'OpenSSH 8.9p1',
        detected: '2026-01-22',
        status: 'Monitoring',
      },
      {
        id: 'CVE-2023-44487',
        severity: 'High',
        component: 'nginx 1.18.0',
        detected: '2026-02-03',
        status: 'Open',
      },
      {
        id: 'CVE-2024-21626',
        severity: 'Medium',
        component: 'Docker 24.0.5',
        detected: '2026-02-10',
        status: 'Monitoring',
      },
      {
        id: 'CVE-2023-38545',
        severity: 'Medium',
        component: 'libcurl 7.88.1',
        detected: '2026-02-18',
        status: 'Resolved',
      },
    ];
    setCves(mockCVEs);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      loadMockData();
      setRefreshing(false);
    }, 1500);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDesc(!sortDesc);
    } else {
      setSortBy(column);
      setSortDesc(true);
    }
  };

  const getSortedCVEs = () => {
    const sorted = [...cves];
    if (sortBy === 'severity') {
      const severityOrder = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };
      sorted.sort((a, b) => {
        return sortDesc 
          ? severityOrder[b.severity] - severityOrder[a.severity]
          : severityOrder[a.severity] - severityOrder[b.severity];
      });
    } else if (sortBy === 'detected') {
      sorted.sort((a, b) => {
        return sortDesc
          ? new Date(b.detected).getTime() - new Date(a.detected).getTime()
          : new Date(a.detected).getTime() - new Date(b.detected).getTime();
      });
    }
    return sorted;
  };

  const getSeverityBadgeStyle = (severity) => {
    const styles = {
      'Critical': {
        background: 'rgba(248,113,113,0.1)',
        color: 'var(--danger)',
        border: '1px solid rgba(248,113,113,0.2)',
      },
      'High': {
        background: 'rgba(251,176,64,0.12)',
        color: 'var(--warn)',
        border: '1px solid rgba(251,176,64,0.2)',
      },
      'Medium': {
        background: 'rgba(96,165,250,0.1)',
        color: 'var(--info)',
        border: '1px solid rgba(96,165,250,0.2)',
      },
      'Low': {
        background: 'rgba(74,222,128,0.1)',
        color: 'var(--ok)',
        border: '1px solid rgba(74,222,128,0.2)',
      },
    };
    return styles[severity] || styles['Low'];
  };

  const getStatusBadgeStyle = (status) => {
    const styles = {
      'Open': {
        background: 'rgba(248,113,113,0.1)',
        color: 'var(--danger)',
      },
      'Monitoring': {
        background: 'rgba(251,176,64,0.12)',
        color: 'var(--warn)',
      },
      'Resolved': {
        background: 'rgba(74,222,128,0.1)',
        color: 'var(--ok)',
      },
    };
    return styles[status] || styles['Open'];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Calculate stats
  const totalCVEs = cves.length;
  const criticalCount = cves.filter(c => c.severity === 'Critical').length;
  const openCount = cves.filter(c => c.status === 'Open').length;
  const resolvedCount = cves.filter(c => c.status === 'Resolved').length;

  if (cves.length === 0) {
    return (
      <div>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              Active CVE Feed
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Known vulnerabilities for your monitored stack
          </p>
        </div>

        {/* Empty state */}
        <div
          className="rounded-lg p-12 flex flex-col items-center justify-center text-center"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <Shield size={64} style={{ color: 'var(--ok)', marginBottom: '24px', opacity: 0.4 }} />
          <h3 className="font-display text-lg font-bold mb-2" style={{ color: 'var(--white)' }}>
            No active CVEs detected
          </h3>
          <p className="font-body text-sm mb-4" style={{ color: 'var(--ts)', maxWidth: '400px' }}>
            Your monitored stack has no known vulnerabilities at this time
          </p>
          <p className="font-mono text-xs" style={{ color: 'var(--te)' }}>
            Last checked: {formatDate(new Date().toISOString())}
          </p>
        </div>
      </div>
    );
  }

  const sortedCVEs = getSortedCVEs();

  return (
    <div>
      {/* Section header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              Active CVE Feed
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Known vulnerabilities for your monitored stack
          </p>
        </div>
        <div className="text-right flex flex-col items-end gap-2">
          <p className="font-mono text-[0.62rem]" style={{ color: 'var(--te)' }}>
            Refreshed: {new Date().toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: 'short', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            className="font-body text-xs px-3 py-1 rounded-[3px]"
            style={{
              background: 'rgba(139,92,246,0.1)',
              border: '1px solid rgba(139,92,246,0.3)',
              color: 'var(--a)',
              height: 'auto',
            }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Summary stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div
          className="p-4 rounded-lg"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
            Total CVEs
          </p>
          <p className="font-display text-2xl font-bold" style={{ color: 'var(--white)' }}>
            {totalCVEs}
          </p>
        </div>

        <div
          className="p-4 rounded-lg"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
            Critical
          </p>
          <p className="font-display text-2xl font-bold" style={{ color: 'var(--danger)' }}>
            {criticalCount}
          </p>
        </div>

        <div
          className="p-4 rounded-lg"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
            Open
          </p>
          <p className="font-display text-2xl font-bold" style={{ color: 'var(--warn)' }}>
            {openCount}
          </p>
        </div>

        <div
          className="p-4 rounded-lg"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-xs mb-1" style={{ color: 'var(--te)' }}>
            Resolved
          </p>
          <p className="font-display text-2xl font-bold" style={{ color: 'var(--ok)' }}>
            {resolvedCount}
          </p>
        </div>
      </div>

      {/* Table */}
      <div
        className="rounded-[10px] overflow-hidden"
        style={{
          background: 'var(--l1)',
          border: '1px solid var(--b1)',
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead
              style={{
                background: 'rgba(255,255,255,0.015)',
                borderBottom: '1px solid var(--b1)',
              }}
            >
              <tr>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    CVE ID
                  </span>
                </th>
                <th className="text-left px-6 py-4 cursor-pointer" onClick={() => handleSort('severity')}>
                  <div className="flex items-center gap-2">
                    <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                      Severity
                    </span>
                    {sortBy === 'severity' && (
                      <span style={{ color: 'var(--a)' }}>{sortDesc ? '↓' : '↑'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    Affected Component
                  </span>
                </th>
                <th className="text-left px-6 py-4 cursor-pointer" onClick={() => handleSort('detected')}>
                  <div className="flex items-center gap-2">
                    <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                      First Detected
                    </span>
                    {sortBy === 'detected' && (
                      <span style={{ color: 'var(--a)' }}>{sortDesc ? '↓' : '↑'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    Status
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedCVEs.map((cve, index) => (
                <tr
                  key={cve.id}
                  className="transition-colors"
                  style={{
                    borderBottom: index < sortedCVEs.length - 1 ? '1px solid var(--b1)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <td className="px-6 py-4">
                    <a
                      href={`https://nvd.nist.gov/vuln/detail/${cve.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-sm font-medium hover:underline"
                      style={{ color: 'var(--white)' }}
                    >
                      {cve.id}
                    </a>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className="inline-block px-3 py-1 rounded-[3px] font-mono text-xs font-medium"
                      style={getSeverityBadgeStyle(cve.severity)}
                    >
                      {cve.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-body text-sm" style={{ color: 'var(--tm)' }}>
                      {cve.component}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm" style={{ color: 'var(--ts)' }}>
                      {formatDate(cve.detected)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className="inline-block px-3 py-1 rounded-[3px] font-mono text-xs font-medium"
                      style={getStatusBadgeStyle(cve.status)}
                    >
                      {cve.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AIBriefingSection() {
  const { authFetch } = useAuth();
  const [briefing, setBriefing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [source, setSource] = useState('');

  useEffect(() => {
    fetchBriefing();
  }, []);

  const fetchBriefing = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`${BACKEND_URL}/api/dashboard/ai-briefing`);

      if (!response.ok) {
        if (response.status === 404) {
          setBriefing(null);
          return;
        }
        throw new Error('Failed to fetch AI briefing');
      }

      const data = await response.json();
      // pending_scan status → no briefing to display yet, show empty state.
      if (data.status === 'pending_scan' || !data.briefing) {
        setBriefing(null);
        return;
      }
      setBriefing(data.briefing);
      setSource(data.source);
    } catch (err) {
      console.error('Error fetching AI briefing:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);

    try {
      const response = await authFetch(
        `${BACKEND_URL}/api/dashboard/ai-briefing/regenerate`,
        { method: 'POST' }
      );
      
      if (response.status === 429) {
        const data = await response.json();
        const retryMinutes = Math.floor(data.detail.retry_after_seconds / 60);
        const retrySeconds = data.detail.retry_after_seconds % 60;
        setToastMessage(`Briefing recently generated — available again in ${retryMinutes}:${retrySeconds.toString().padStart(2, '0')}`);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 4000);
        return;
      }
      
      if (!response.ok) {
        throw new Error('Generation failed');
      }
      
      const data = await response.json();
      setBriefing(data.briefing);
      setSource(data.source);
      
      setToastMessage('New briefing generated');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    } catch (err) {
      console.error('Error generating briefing:', err);
      setToastMessage('Generation failed — please try again');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (loading) {
    return (
      <div>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              AI Security Briefing
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Weekly security intelligence briefing
          </p>
        </div>

        {/* Loading skeleton */}
        <div
          className="rounded-lg p-12 flex items-center justify-center"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <div className="flex flex-col items-center gap-4">
            <div 
              className="animate-pulse rounded-full"
              style={{
                width: '48px',
                height: '48px',
                background: 'rgba(139,92,246,0.1)'
              }}
            />
            <p className="font-body text-sm" style={{ color: 'var(--ts)' }}>
              Loading security briefing...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              AI Security Briefing
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Weekly security intelligence briefing
          </p>
        </div>

        {/* Error state */}
        <div
          className="rounded-lg p-12 flex flex-col items-center justify-center text-center"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
            style={{ background: 'rgba(239,68,68,0.1)' }}
          >
            <Sparkles size={24} style={{ color: 'var(--danger)' }} />
          </div>
          <h3 className="font-display text-lg font-bold mb-2" style={{ color: 'var(--white)' }}>
            Briefing temporarily unavailable
          </h3>
          <p className="font-body text-sm mb-6" style={{ color: 'var(--ts)', maxWidth: '400px' }}>
            {error}
          </p>
          <Button
            onClick={fetchBriefing}
            className="font-body text-sm px-6 py-2 rounded-[3px]"
            style={{ background: 'var(--a)', color: '#fff' }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!briefing) {
    return (
      <div>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              AI Security Briefing
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Weekly security intelligence briefing
          </p>
        </div>

        {/* Empty state */}
        <div
          data-testid="ai-briefing-empty-state"
          className="rounded-lg p-12 flex flex-col items-center justify-center text-center"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <Sparkles size={64} style={{ color: 'var(--a)', marginBottom: '24px', opacity: 0.4 }} />
          <h3 className="font-display text-lg font-bold mb-2" style={{ color: 'var(--white)' }}>
            Your AI briefing will be ready after your first scan completes
          </h3>
          <p className="font-body text-sm" style={{ color: 'var(--ts)', maxWidth: '420px' }}>
            Once your first scan runs we&apos;ll generate a plain-English briefing of what we found
            and what to prioritise.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Section header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles size={24} style={{ color: 'var(--a)' }} />
          <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
            AI Security Briefing
          </h2>
        </div>
        <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
          Weekly security intelligence briefing
        </p>
      </div>

      {/* Premium purple card */}
      <div
        className="rounded-[10px] overflow-hidden transition-opacity"
        style={{
          background: 'var(--l1)',
          border: '1px solid rgba(139,92,246,0.18)',
          boxShadow: '0 0 60px rgba(139,92,246,0.07)',
          opacity: generating ? 0.4 : 1,
        }}
      >
        {/* Card header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{
            background: 'rgba(0,0,0,0.2)',
            borderBottom: '1px solid rgba(139,92,246,0.1)',
          }}
        >
          <div className="flex items-center gap-2">
            <Sparkles size={20} style={{ color: 'var(--a)' }} />
            <span 
              className="font-mono text-[0.62rem] font-bold uppercase tracking-[0.15em]"
              style={{ color: 'rgba(139,92,246,0.5)' }}
            >
              AI Security Briefing
            </span>
          </div>

          {/* Live badge */}
          <div
            className="flex items-center gap-2 px-3 py-1 rounded-full"
            style={{
              background: 'rgba(139,92,246,0.08)',
              border: '1px solid rgba(139,92,246,0.2)',
            }}
          >
            <div className="relative w-1.5 h-1.5">
              <div
                className="absolute inset-0 rounded-full animate-pulse-dot"
                style={{ background: 'var(--a)' }}
              />
            </div>
            <span className="font-mono text-[0.6rem]" style={{ color: 'var(--a)' }}>
              Live
            </span>
          </div>
        </div>

        {/* Briefing content */}
        <div className="p-6">
          {/* Headline */}
          <div className="mb-6">
            <p 
              className="font-mono text-[0.6rem] uppercase tracking-[0.2em] mb-2"
              style={{ color: 'var(--a)', opacity: 0.6 }}
            >
              Weekly Security Brief
            </p>
            <h3 className="font-display text-[1.1rem] font-bold" style={{ color: 'var(--white)' }}>
              {briefing.headline}
            </h3>
          </div>

          <div 
            className="mb-6"
            style={{ 
              height: '1px', 
              background: 'rgba(139,92,246,0.08)' 
            }} 
          />

          {/* Three findings */}
          <div className="space-y-6 mb-6">
            {briefing.findings.map((finding, index) => (
              <div
                key={index}
                className="pl-4"
                style={{
                  borderLeft: '2px solid rgba(139,92,246,0.2)',
                }}
              >
                <div className="flex items-start gap-3 mb-2">
                  <span 
                    className="font-mono text-[0.65rem]"
                    style={{ color: 'var(--a)', opacity: 0.5 }}
                  >
                    {finding.number}
                  </span>
                  <div className="flex-1">
                    <h4 className="font-display text-[0.88rem] font-semibold mb-1" style={{ color: 'var(--white)' }}>
                      {finding.title}
                    </h4>
                    <p className="font-body text-[0.82rem] mb-2" style={{ color: 'var(--tm)', lineHeight: '1.7', fontWeight: 300 }}>
                      <span className="font-medium">Risk:</span> {finding.risk}
                    </p>
                    <p className="font-body text-[0.82rem]" style={{ color: 'var(--ts)', lineHeight: '1.7', fontWeight: 300 }}>
                      <span className="font-medium">Why it matters:</span> {finding.why_it_matters}
                    </p>
                  </div>
                </div>
                {index < briefing.findings.length - 1 && (
                  <div 
                    className="mt-6"
                    style={{ 
                      height: '1px', 
                      background: 'rgba(139,92,246,0.06)' 
                    }} 
                  />
                )}
              </div>
            ))}
          </div>

          {/* Recommended action */}
          <div
            className="p-4 rounded-md"
            style={{
              background: 'rgba(139,92,246,0.06)',
              border: '1px solid rgba(139,92,246,0.15)',
            }}
          >
            <p 
              className="font-mono text-[0.58rem] uppercase tracking-[0.15em] mb-2"
              style={{ color: 'var(--a)' }}
            >
              Recommended Action
            </p>
            <div className="flex items-start gap-2">
              <span style={{ color: 'var(--a)', marginTop: '2px' }}>→</span>
              <p className="font-body text-[0.84rem]" style={{ color: 'var(--white)', fontWeight: 400 }}>
                {briefing.recommended_action}
              </p>
            </div>
          </div>
        </div>

        {/* Card footer */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{
            borderTop: '1px solid rgba(139,92,246,0.08)',
          }}
        >
          <div className="flex items-center gap-3">
            <p className="font-mono text-[0.62rem]" style={{ color: 'var(--te)' }}>
              Last generated: {formatDate(briefing.generated_at)}
            </p>
            <span 
              className="font-mono text-[0.62rem] px-2 py-1 rounded-[3px]"
              style={{ 
                color: 'var(--ts)',
                background: source === 'cached' ? 'rgba(96,165,250,0.08)' : 'rgba(139,92,246,0.08)',
                border: source === 'cached' ? '1px solid rgba(96,165,250,0.2)' : '1px solid rgba(139,92,246,0.2)'
              }}
            >
              {source === 'cached' ? 'cached' : 'freshly generated'}
            </span>
          </div>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="font-body text-xs px-4 py-2 rounded-[3px] transition-all"
            style={{
              background: 'transparent',
              border: '1px solid rgba(139,92,246,0.25)',
              color: generating ? 'var(--ts)' : 'var(--ts)',
              height: 'auto',
            }}
            onMouseEnter={(e) => {
              if (!generating) {
                e.currentTarget.style.borderColor = 'var(--a)';
                e.currentTarget.style.color = 'var(--white)';
              }
            }}
            onMouseLeave={(e) => {
              if (!generating) {
                e.currentTarget.style.borderColor = 'rgba(139,92,246,0.25)';
                e.currentTarget.style.color = 'var(--ts)';
              }
            }}
            data-testid="generate-briefing-btn"
          >
            {generating ? 'Generating...' : 'Generate New Briefing'}
          </Button>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div
          className="fixed bottom-6 right-6 px-6 py-3 rounded-[6px] font-body text-sm shadow-lg z-50 flex items-center gap-3"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
            color: 'var(--white)',
          }}
        >
          <div 
            className="w-2 h-2 rounded-full"
            style={{ background: toastMessage.includes('failed') ? 'var(--danger)' : 'var(--ok)' }}
          />
          {toastMessage}
        </div>
      )}
    </div>
  );
}

function ScanHistorySection() {
  const { authFetch } = useAuth();
  const [scans, setScans] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalScans, setTotalScans] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [verifiedDomains, setVerifiedDomains] = useState([]);
  const rowsPerPage = 10;

  // Fetch verified domains on mount
  useEffect(() => {
    fetchVerifiedDomains();
  }, []);

  // Fetch scans when page or domain filter changes
  useEffect(() => {
    fetchScanHistory();
  }, [currentPage, selectedDomain]);

  const fetchVerifiedDomains = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/verify/domains`);

      if (response.ok) {
        const data = await response.json();
        const isVerified = (d) =>
          d?.verified === true || d?.verified === 'true' || d?.verified === 1;
        setVerifiedDomains((data.domains || []).filter(isVerified));
      }
    } catch (err) {
      console.error('Failed to fetch domains:', err);
    }
  };

  const fetchScanHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ page: currentPage.toString() });

      if (selectedDomain) {
        params.append('domain', selectedDomain);
      }

      const response = await authFetch(
        `${BACKEND_URL}/api/dashboard/scan-history?${params}`,
      );

      if (!response.ok) {
        throw new Error('Failed to fetch scan history');
      }

      const data = await response.json();
      setScans(data.scans);
      setTotalScans(data.total);
      setTotalPages(data.pages);
    } catch (err) {
      console.error('Error fetching scan history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getGradeColor = (grade) => {
    if (grade === 'A' || grade === 'A+') return 'var(--ok)';
    if (grade === 'B') return 'var(--warn)';
    return 'var(--danger)';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'var(--ok)';
    if (score >= 70) return 'var(--warn)';
    return 'var(--danger)';
  };

  const getCVEColor = (count) => {
    if (count === 0) return 'var(--ok)';
    if (count <= 3) return 'var(--warn)';
    return 'var(--danger)';
  };

  const handleExport = () => {
    setToastMessage('Exporting scan history...');
    setShowToast(true);
    setTimeout(() => {
      setToastMessage('Export complete');
      setTimeout(() => setShowToast(false), 2500);
    }, 1500);
  };

  // ── PDF report actions ──
  // Auth is header-based (Bearer in localStorage), so window.open can't carry
  // the token — we fetch the PDF via authFetch as a blob, then open/download it.
  const [pdfBusy, setPdfBusy] = useState(null); // `${scanId}:${action}` while in-flight

  const toast = (msg) => {
    setToastMessage(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  const fetchPdfBlobUrl = async (scanId, mode) => {
    const res = await authFetch(`${BACKEND_URL}/api/scans/${scanId}/pdf/${mode}`);
    if (!res.ok) throw new Error(`PDF ${mode} failed`);
    const blob = await res.blob();
    return window.URL.createObjectURL(blob);
  };

  const handlePdfDownload = async (scanId, domain) => {
    setPdfBusy(`${scanId}:download`);
    try {
      const url = await fetchPdfBlobUrl(scanId, 'download');
      const date = new Date().toISOString().split('T')[0];
      const a = document.createElement('a');
      a.href = url;
      a.download = `acodia-scan-${domain}-${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      toast('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast('PDF download failed');
    } finally {
      setPdfBusy(null);
    }
  };

  const handlePdfView = async (scanId) => {
    setPdfBusy(`${scanId}:view`);
    try {
      const url = await fetchPdfBlobUrl(scanId, 'view');
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error(err);
      toast('Couldn’t open PDF');
    } finally {
      setPdfBusy(null);
    }
  };

  const handlePdfEmail = async (scanId) => {
    setPdfBusy(`${scanId}:email`);
    toast('Sending email…');
    try {
      const res = await authFetch(`${BACKEND_URL}/api/scans/${scanId}/pdf/email`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      toast(res.ok && data.success ? 'Sent! Check your inbox.' : "Couldn't send email");
    } catch (err) {
      console.error(err);
      toast("Couldn't send email");
    } finally {
      setPdfBusy(null);
    }
  };

  const showingStart = totalScans > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const showingEnd = Math.min(currentPage * rowsPerPage, totalScans);

  // Loading state
  if (loading) {
    return (
      <div>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <History size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              Scan History
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Complete record of all security scans
          </p>
        </div>

        {/* Loading skeleton */}
        <div
          className="rounded-lg p-12 flex items-center justify-center"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <div className="flex flex-col items-center gap-4">
            <div 
              className="animate-pulse rounded-full"
              style={{
                width: '48px',
                height: '48px',
                background: 'rgba(139,92,246,0.1)'
              }}
            />
            <p className="font-body text-sm" style={{ color: 'var(--ts)' }}>
              Loading scan history...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <History size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              Scan History
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Complete record of all security scans
          </p>
        </div>

        {/* Error state */}
        <div
          className="rounded-lg p-12 flex flex-col items-center justify-center text-center"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
            style={{ background: 'rgba(239,68,68,0.1)' }}
          >
            <History size={24} style={{ color: 'var(--danger)' }} />
          </div>
          <h3 className="font-display text-lg font-bold mb-2" style={{ color: 'var(--white)' }}>
            Failed to load scan history
          </h3>
          <p className="font-body text-sm mb-6" style={{ color: 'var(--ts)', maxWidth: '400px' }}>
            {error}
          </p>
          <Button
            onClick={fetchScanHistory}
            className="font-body text-sm px-6 py-2 rounded-[3px]"
            style={{ background: 'var(--a)', color: '#fff' }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Empty state
  if (scans.length === 0) {
    return (
      <div>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <History size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              Scan History
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Complete record of all security scans
          </p>
        </div>

        {/* Empty state */}
        <div
          className="rounded-lg p-12 flex flex-col items-center justify-center text-center"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
          }}
        >
          <History size={64} style={{ color: 'var(--a)', marginBottom: '24px', opacity: 0.3 }} />
          <h3 className="font-display text-lg font-bold mb-2" style={{ color: 'var(--white)' }}>
            No scans yet
          </h3>
          <p className="font-body text-sm mb-6" style={{ color: 'var(--ts)', maxWidth: '420px' }}>
            {selectedDomain
              ? `No scans found for ${selectedDomain}`
              : 'Your first scan will run automatically within the next few hours once your domain is verified.'}
          </p>
          {selectedDomain ? (
            <Button
              onClick={() => setSelectedDomain('')}
              className="font-body text-sm px-6 py-2 rounded-[3px]"
              style={{ background: 'var(--a)', color: '#fff' }}
            >
              Clear Filter
            </Button>
          ) : (
            <a
              href="/#scanner"
              data-testid="scan-history-empty-cta"
              className="inline-flex items-center gap-2 font-body text-sm px-5 h-10 rounded-[3px]"
              style={{ background: 'var(--a)', color: 'var(--white)' }}
            >
              Run a manual scan <ArrowRight size={14} />
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Section header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <History size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              Scan History
            </h2>
          </div>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Complete record of all security scans
          </p>
        </div>
        <Button
          onClick={handleExport}
          className="font-body text-xs px-4 py-2 rounded-[3px]"
          style={{
            background: 'rgba(139,92,246,0.1)',
            border: '1px solid rgba(139,92,246,0.3)',
            color: 'var(--a)',
            height: 'auto',
          }}
        >
          Export CSV
        </Button>
      </div>

      {/* Domain Filter */}
      {verifiedDomains.length > 1 && (
        <div className="mb-4">
          <select
            value={selectedDomain}
            onChange={(e) => {
              setSelectedDomain(e.target.value);
              setCurrentPage(1); // Reset to first page on filter change
            }}
            className="font-body text-sm px-4 py-2 rounded-[3px]"
            style={{
              background: 'var(--l1)',
              border: '1px solid var(--b1)',
              color: 'var(--white)',
            }}
            data-testid="domain-filter-dropdown"
          >
            <option value="">All Domains</option>
            {verifiedDomains.map((domain) => (
              <option key={domain.id} value={domain.domain}>
                {domain.domain}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Table */}
      <div
        className="rounded-[10px] overflow-hidden"
        style={{
          background: 'var(--l1)',
          border: '1px solid var(--b1)',
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead
              style={{
                background: 'rgba(255,255,255,0.015)',
                borderBottom: '1px solid var(--b1)',
              }}
            >
              <tr>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    Date
                  </span>
                </th>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    Domain
                  </span>
                </th>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    Type
                  </span>
                </th>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    Overall Grade
                  </span>
                </th>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    TLS Grade
                  </span>
                </th>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    Header Score
                  </span>
                </th>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    CVE Count
                  </span>
                </th>
                <th className="text-left px-6 py-4">
                  <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                    Download
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {scans.map((scan, index) => (
                <tr
                  key={scan.id}
                  className="transition-colors"
                  style={{
                    background: index % 2 === 0 ? 'var(--l3)' : 'var(--l4)',
                    borderBottom: '1px solid var(--b1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.025)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = index % 2 === 0 ? 'var(--l3)' : 'var(--l4)';
                  }}
                  data-testid={`scan-row-${scan.id}`}
                >
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm" style={{ color: 'var(--ts)' }}>
                      {formatDate(scan.created_at)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm font-medium" style={{ color: 'var(--white)' }}>
                      {scan.domain}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className="font-body text-[0.65rem] px-2 py-1 rounded-[3px] font-medium uppercase tracking-wide"
                      style={{
                        background: scan.scan_type === 'active' 
                          ? 'rgba(139,92,246,0.08)' 
                          : 'rgba(96,165,250,0.1)',
                        border: scan.scan_type === 'active'
                          ? '1px solid var(--b2)'
                          : '1px solid rgba(96,165,250,0.2)',
                        color: scan.scan_type === 'active' ? 'var(--a)' : 'var(--info)',
                      }}
                    >
                      {scan.scan_type === 'active' ? 'Acodia Watch' : 'Free Scan'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className="font-display text-lg font-bold"
                      style={{ color: getGradeColor(scan.overall_grade) }}
                    >
                      {scan.overall_grade}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className="font-mono text-sm font-medium"
                      style={{ color: getGradeColor(scan.tls_grade) }}
                    >
                      {scan.tls_grade}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className="font-mono text-sm font-medium"
                      style={{ color: getScoreColor(scan.header_score) }}
                    >
                      {scan.header_score}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className="font-mono text-sm font-medium"
                      style={{ color: getCVEColor(scan.cve_count) }}
                    >
                      {scan.cve_count}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      {/* View PDF in new tab */}
                      <button
                        type="button"
                        onClick={() => handlePdfView(scan.id)}
                        disabled={pdfBusy === `${scan.id}:view`}
                        title="View report"
                        aria-label="View PDF report"
                        data-testid={`pdf-view-btn-${scan.id}`}
                        className="p-1.5 rounded-[3px] transition-colors"
                        style={{ border: '1px solid var(--b1)', color: 'var(--ts)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--a)'; e.currentTarget.style.color = 'var(--a)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--b1)'; e.currentTarget.style.color = 'var(--ts)'; }}
                      >
                        {pdfBusy === `${scan.id}:view`
                          ? <Loader2 size={15} className="animate-spin" />
                          : <Eye size={15} />}
                      </button>
                      {/* Download PDF */}
                      <button
                        type="button"
                        onClick={() => handlePdfDownload(scan.id, scan.domain)}
                        disabled={pdfBusy === `${scan.id}:download`}
                        title="Download PDF report"
                        aria-label="Download PDF report"
                        data-testid={`pdf-download-btn-${scan.id}`}
                        className="p-1.5 rounded-[3px] transition-colors"
                        style={{ border: '1px solid var(--b1)', color: 'var(--ts)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--a)'; e.currentTarget.style.color = 'var(--a)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--b1)'; e.currentTarget.style.color = 'var(--ts)'; }}
                      >
                        {pdfBusy === `${scan.id}:download`
                          ? <Loader2 size={15} className="animate-spin" />
                          : <FileText size={15} />}
                      </button>
                      {/* Email me this report */}
                      <button
                        type="button"
                        onClick={() => handlePdfEmail(scan.id)}
                        disabled={pdfBusy === `${scan.id}:email`}
                        title="Email me this report"
                        aria-label="Email me this report"
                        data-testid={`pdf-email-btn-${scan.id}`}
                        className="p-1.5 rounded-[3px] transition-colors"
                        style={{ border: '1px solid var(--b1)', color: 'var(--ts)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--a)'; e.currentTarget.style.color = 'var(--a)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--b1)'; e.currentTarget.style.color = 'var(--ts)'; }}
                      >
                        {pdfBusy === `${scan.id}:email`
                          ? <Loader2 size={15} className="animate-spin" />
                          : <Mail size={15} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{
            borderTop: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-[0.72rem]" style={{ color: 'var(--ts)' }}>
            Showing {showingStart}–{showingEnd} of {totalScans} scans
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-[3px] font-body text-xs transition-colors"
              style={{
                background: 'transparent',
                border: '1px solid var(--b1)',
                color: currentPage === 1 ? 'var(--te)' : 'var(--ts)',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage === 1 ? 0.5 : 1,
              }}
              data-testid="pagination-prev-btn"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-[3px] font-body text-xs transition-colors"
              style={{
                background: 'transparent',
                border: '1px solid var(--b1)',
                color: currentPage === totalPages ? 'var(--te)' : 'var(--ts)',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                opacity: currentPage === totalPages ? 0.5 : 1,
              }}
              data-testid="pagination-next-btn"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div
          className="fixed bottom-6 right-6 px-6 py-3 rounded-[6px] font-body text-sm shadow-lg z-50 flex items-center gap-3"
          style={{
            background: 'var(--l1)',
            border: '1px solid var(--b1)',
            color: 'var(--white)',
          }}
        >
          <div 
            className="w-2 h-2 rounded-full"
            style={{ background: 'var(--ok)' }}
          />
          {toastMessage}
        </div>
      )}
    </div>
  );
}

function DomainsSection() {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [plan, setPlan] = useState(null);
  const [maxDomains, setMaxDomains] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/dashboard/domains`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail?.error || data?.detail || 'Failed to load domains');
      setRows(Array.isArray(data.domains) ? data.domains : []);
      setPlan(data.plan ?? null);
      setMaxDomains(data.max_domains ?? null);
    } catch (e) {
      setError(e.message || 'Failed to load domains.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerified = () => {
    load();
  };

  const gridCols = (() => {
    const n = rows.length;
    if (n <= 1) return 'grid-cols-1';
    if (n === 2) return 'grid-cols-1 md:grid-cols-2';
    if (n === 3) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    return 'grid-cols-1 md:grid-cols-2';
  })();

  return (
    <div id="domain-verification-grid">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Globe size={24} style={{ color: 'var(--a)' }} />
            <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
              Domain Verification
            </h2>
          </div>
          <p className="font-body text-[0.78rem]" style={{ color: 'var(--ts)' }}>
            Verify each domain so Acodia Watch can start continuous monitoring.
            {plan ? ` Plan ${plan} — up to ${maxDomains} domains.` : ''}
          </p>
        </div>
        <a
          href="/watch"
          className="inline-flex items-center gap-1.5 font-body text-xs px-3 py-2 rounded-[3px]"
          style={{ background: 'transparent', border: '1px solid var(--b2)', color: 'var(--ts)' }}
        >
          <Plus size={12} /> Add / change domains
        </a>
      </div>

      {loading && (
        <div
          className="p-10 rounded-lg text-center"
          style={{ background: 'var(--l1)', border: '1px solid var(--b1)', color: 'var(--ts)' }}
        >
          <span className="font-mono text-xs">Loading your domains…</span>
        </div>
      )}

      {!loading && error && (
        <div
          className="p-5 rounded-lg flex items-start gap-3"
          style={{ background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.25)' }}
        >
          <AlertCircle size={18} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="font-body font-medium" style={{ color: 'var(--white)' }}>Could not load domains.</p>
            <p className="font-body text-sm mt-1" style={{ color: 'rgba(240,237,248,0.75)' }}>{error}</p>
          </div>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div
          data-testid="domains-empty-state"
          className="rounded-[10px] p-12 flex flex-col items-center justify-center text-center"
          style={{ background: 'var(--l1)', border: '1px solid var(--b1)' }}
        >
          <Globe size={48} style={{ color: 'var(--a)', marginBottom: 16, opacity: 0.5 }} />
          <h3 className="font-display text-lg font-bold mb-2" style={{ color: 'var(--white)' }}>
            No domains added yet
          </h3>
          <p className="font-body text-sm mb-6" style={{ color: 'var(--ts)', maxWidth: 420 }}>
            Add your website to start monitoring.
          </p>
          <button
            type="button"
            onClick={() => {
              try { window.localStorage.removeItem('acodia_onboarded'); } catch { /* ignore */ }
              window.location.reload();
            }}
            className="inline-flex items-center gap-2 font-body text-sm px-5 h-10 rounded-[3px]"
            style={{ background: 'var(--a)', color: 'var(--white)', border: 'none', cursor: 'pointer' }}
          >
            <Plus size={14} /> Add domain →
          </button>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div className={`grid ${gridCols} gap-5`}>
            {rows.map((d, i) => {
              // On plans of 5, let the last card span full width when the grid is 2-cols
              const spanFull = rows.length === 5 && i === 4 ? 'md:col-span-2' : '';
              return (
                <div key={d.id} className={spanFull}>
                  <DomainVerificationCard
                    domain={d}
                    authFetch={authFetch}
                    backendUrl={BACKEND_URL}
                    onVerified={handleVerified}
                  />
                </div>
              );
            })}
          </div>

          {rows.some((d) => Array.isArray(d.recommendations) && d.recommendations.length > 0) && (
            <div className="mt-6 space-y-5">
              {rows
                .filter((d) => Array.isArray(d.recommendations) && d.recommendations.length > 0)
                .map((d) => (
                  <div key={`recs-${d.id}`}>
                    <p
                      className="font-mono uppercase mb-2"
                      style={{ fontSize: '0.65rem', letterSpacing: '0.12em', color: 'var(--ts)' }}
                    >
                      {d.domain}
                    </p>
                    <DashboardRecommendationsPanel
                      recommendations={d.recommendations}
                      testId={`dashboard-recommendations-${d.id}`}
                    />
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


function BillingSection({ subscription, onCancelled }) {
  const { authFetch } = useAuth();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  // Real Stripe data, fetched on mount (never derived from placeholders).
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);

  const status = subscription?.status || 'inactive';
  // Normalise legacy integer plan values (1/3/5) from older subscription rows.
  const LEGACY_PLAN_MAP = { 1: 'essential', 3: 'professional', 5: 'business' };
  const rawPlan = subscription?.plan ?? 'essential';
  const plan = LEGACY_PLAN_MAP[rawPlan] || rawPlan;
  const BASE_PRICE = { essential: 49, professional: 99, business: 199 };
  const price = subscription?.price_per_month || BASE_PRICE[plan] || 49;
  const trialEndsAt = subscription?.trial_ends_at;
  const currentPeriodEnd = subscription?.current_period_end;
  const cancelAt = subscription?.cancel_at;
  const accessUntil = cancelAt || trialEndsAt || currentPeriodEnd;

  const isTrial = status === 'trial';
  const isCancelling = status === 'cancelling';
  const isCancelled = status === 'cancelled' || status === 'expired';
  const isPastDue = status === 'past_due';

  // Derive trial end date and days remaining from subscription data.
  const trialEndDate = trialEndsAt ? new Date(trialEndsAt) : null;
  const trialEndValid = trialEndDate && !Number.isNaN(trialEndDate.getTime());
  const formattedTrialEnd = trialEndValid
    ? trialEndDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const daysLeft = trialEndValid
    ? Math.max(0, Math.ceil((trialEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 7;

  // Capitalise a Stripe card brand for display ("visa" -> "Visa").
  const formatCardBrand = (brand) =>
    brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';
  const formatCardExpiry = (m, y) =>
    `${String(m ?? '').padStart(2, '0')}/${String(y ?? '').slice(-2)}`;

  const planName =
    plan === 'business' ? 'Acodia Watch Business' :
    plan === 'professional' ? 'Acodia Watch Professional' :
    'Acodia Watch Essential';

  const toast = (msg) => {
    setToastMessage(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  // Surface the result of a returning Stripe Checkout redirect, then strip the
  // query params so a refresh doesn't re-toast.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const checkout = params.get('checkout');
      if (checkout === 'success') {
        toast('Welcome to Acodia Watch — your subscription is active');
      } else if (checkout === 'cancel') {
        toast('Checkout cancelled — your trial is still active');
      }
      if (checkout) {
        params.delete('checkout');
        params.delete('session_id');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load real payment methods and invoices from Stripe on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/billing/payment-methods`);
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && Array.isArray(data?.payment_methods)) {
          setPaymentMethods(data.payment_methods);
        }
      } catch { /* ignore — falls back to empty state */ }
    })();
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/billing/invoices`);
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && Array.isArray(data?.invoices)) {
          setInvoices(data.invoices);
        }
      } catch { /* ignore — falls back to empty state */ }
      finally { if (!cancelled) setInvoicesLoaded(true); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start a paid subscription via Stripe Checkout (trial → paid conversion).
  const startCheckout = async (planId) => {
    setCheckoutLoadingPlan(planId);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planId, interval: billingInterval }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.checkout_url) {
        toast((typeof data?.detail === 'string' && data.detail) || 'Could not start checkout. Please try again.');
        return;
      }
      window.location.href = data.checkout_url;
    } catch {
      toast('Could not start checkout. Please try again.');
    } finally {
      setCheckoutLoadingPlan(null);
    }
  };

  // Open the Stripe Customer Portal (manage / switch / cancel / update card).
  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/billing/create-portal-session`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.portal_url) {
        toast((typeof data?.detail === 'string' && data.detail) || 'Could not open billing portal.');
        return;
      }
      window.location.href = data.portal_url;
    } catch {
      toast('Could not open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancelSubscription = () => setShowCancelModal(true);
  const handleCancelModal = () => setShowCancelModal(false);

  const handleConfirmCancel = async () => {
    setCancelling(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/subscription/cancel`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast(data?.detail?.error || 'Cancellation failed');
        return;
      }
      setShowCancelModal(false);
      toast(`Subscription cancelled — monitoring continues until ${formatDate(data.cancel_at)}`);
      onCancelled?.();
    } catch (err) {
      toast('Network error. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const features = [
    'Real-time threat monitoring',
    '1 verified domain included',
    'Unlimited security scans',
    'Weekly security intelligence briefing',
    'TLS & header analysis',
    'CVE vulnerability tracking',
  ];

  return (
    <div>
      {/* Section header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <CreditCard size={24} style={{ color: 'var(--a)' }} />
          <h2 className="font-display text-[1.3rem] font-extrabold" style={{ color: 'var(--white)' }}>
            Billing and Subscription
          </h2>
        </div>
        <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
          Manage your Acodia Watch subscription and billing
        </p>
      </div>

      {/* Upgrade picker — trial users choose a paid plan without leaving the dashboard */}
      {isTrial && (
        <div
          data-testid="billing-upgrade-section"
          className="rounded-[10px] p-6 mb-4"
          style={{
            background: 'var(--l1)',
            border: `1px solid ${daysLeft <= 3 ? 'rgba(248,113,113,0.35)' : 'var(--b2)'}`,
          }}
        >
          <div className="mb-5">
            <h3
              className="font-display font-bold mb-1"
              style={{ color: daysLeft <= 3 ? 'var(--danger)' : 'var(--white)', fontSize: '1.5rem' }}
            >
              {daysLeft} day{daysLeft === 1 ? '' : 's'} left in your trial
            </h3>
            <p className="font-body text-sm" style={{ color: daysLeft <= 3 ? 'var(--danger)' : 'var(--a)' }}>
              {daysLeft <= 3
                ? 'Your trial ends soon — choose a plan to keep access'
                : 'Choose a plan before your trial ends to keep monitoring active'}
            </p>
          </div>

          <div className="mb-5">
            <IntervalToggle interval={billingInterval} onChange={setBillingInterval} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {UPGRADE_PLANS.map((p) => {
              const loading = checkoutLoadingPlan === p.id;
              const priceMain = billingInterval === 'annual' ? p.annual : p.monthly;
              const cadence = billingInterval === 'annual' ? '/yr' : '/mo';
              return (
                <div
                  key={p.id}
                  data-testid={`upgrade-plan-${p.id}`}
                  className="relative rounded-[8px] p-5 flex flex-col"
                  style={{
                    background: 'var(--l2)',
                    border: '1px solid var(--b1)',
                    borderTop: p.popular ? '2px solid var(--a)' : '1px solid var(--b1)',
                    transform: p.popular ? 'scale(1.02)' : 'none',
                  }}
                >
                  <h4 className="font-display text-base font-bold mb-1" style={{ color: 'var(--white)' }}>
                    Acodia Watch {p.name}
                  </h4>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="font-display text-2xl font-extrabold" style={{ color: 'var(--white)' }}>{priceMain}</span>
                    <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>{cadence}</span>
                  </div>
                  {p.valueProp && (
                    <p className="font-body text-[0.78rem] mb-3" style={{ color: 'var(--tm)', lineHeight: 1.5 }}>
                      {p.valueProp}
                    </p>
                  )}
                  <ul className="space-y-1.5 mb-5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 font-body text-[0.78rem]" style={{ color: 'var(--tm)' }}>
                        <Check size={13} style={{ color: 'var(--ok)', marginTop: 3, flexShrink: 0 }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    type="button"
                    data-testid={`upgrade-choose-${p.id}`}
                    onClick={() => startCheckout(p.id)}
                    disabled={Boolean(checkoutLoadingPlan)}
                    className="font-body font-medium text-sm h-10 rounded-[3px] w-full justify-center"
                    style={{
                      background: p.popular ? 'var(--a)' : 'transparent',
                      border: p.popular ? 'none' : '1px solid var(--b2)',
                      color: '#fff',
                      opacity: checkoutLoadingPlan && !loading ? 0.6 : 1,
                    }}
                  >
                    {loading ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin" /> Redirecting…
                      </span>
                    ) : `Choose ${p.name}`}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cancelling banner (access continues until period end) */}
      {isCancelling && (
        <div
          className="rounded-[10px] p-4 mb-4 flex items-start justify-between gap-4 flex-wrap"
          style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)' }}
        >
          <div className="flex items-start gap-2">
            <AlertCircle size={18} style={{ color: 'var(--warn)', marginTop: 1, flexShrink: 0 }} />
            <p className="font-body text-sm" style={{ color: 'var(--warn)' }}>
              Subscription ends on {formatDate(accessUntil) || 'the end of your current period'}. You will continue to have access until then.
            </p>
          </div>
          <Button
            type="button"
            onClick={openPortal}
            disabled={portalLoading}
            className="font-body text-sm px-4 py-2 rounded-[3px] whitespace-nowrap"
            style={{ background: 'transparent', border: '1px solid var(--b2)', color: 'var(--white)' }}
          >
            {portalLoading ? 'Opening…' : 'Reactivate subscription'}
          </Button>
        </div>
      )}

      {/* Past-due banner (payment failed) */}
      {isPastDue && (
        <div
          className="rounded-[10px] p-4 mb-4 flex items-start justify-between gap-4 flex-wrap"
          style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)' }}
        >
          <div className="flex items-start gap-2">
            <AlertCircle size={18} style={{ color: 'var(--danger)', marginTop: 1, flexShrink: 0 }} />
            <p className="font-body text-sm" style={{ color: 'var(--danger)' }}>
              Payment failed — please update your card to keep your subscription active.
            </p>
          </div>
          <Button
            type="button"
            onClick={openPortal}
            disabled={portalLoading}
            className="font-body text-sm px-4 py-2 rounded-[3px] whitespace-nowrap"
            style={{ background: 'var(--danger)', color: '#fff' }}
          >
            {portalLoading ? 'Opening…' : 'Update payment method'}
          </Button>
        </div>
      )}

      {/* Cancelled / expired banner */}
      {isCancelled && (
        <div
          className="rounded-[10px] p-4 mb-4 flex items-center justify-between gap-4 flex-wrap"
          style={{ background: 'var(--l2)', border: '1px solid var(--b1)' }}
        >
          <p className="font-body text-sm" style={{ color: 'var(--ts)' }}>
            Your subscription has ended.
          </p>
          <a
            href="/watch"
            className="font-body text-sm px-4 py-2 rounded-[3px] whitespace-nowrap"
            style={{ background: 'var(--a)', color: '#fff', textDecoration: 'none' }}
          >
            Resubscribe
          </a>
        </div>
      )}

      {/* Card 1: Current Plan */}
      <div
        className="rounded-[10px] p-6 mb-4"
        style={{
          background: 'var(--l1)',
          border: '1px solid var(--b1)',
        }}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="font-display text-lg font-bold" style={{ color: 'var(--white)' }}>
                {planName}
              </h3>
              <span
                className="px-3 py-1 rounded-[3px] font-body text-[0.65rem] font-bold uppercase tracking-wider"
                style={(() => {
                  if (isTrial) return { background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)', color: 'var(--ok)' };
                  if (isCancelling) return { background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)', color: 'var(--warn)' };
                  if (isCancelled) return { background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.25)', color: 'var(--danger)' };
                  return { background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)', color: 'var(--ok)' };
                })()}
              >
                {isTrial
                  ? `TRIAL · ${daysLeft}D LEFT`
                  : isCancelling
                  ? 'Cancelling'
                  : isCancelled
                  ? 'Cancelled'
                  : 'Active'}
              </span>
            </div>
            {isTrial && (
              <p
                className="mb-2"
                style={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: 'var(--ts)' }}
              >
                Your card will not be charged until your trial ends. Cancel any time before{' '}
                <span style={{ color: 'var(--white)', fontWeight: 500 }}>
                  {formattedTrialEnd || '—'}
                </span>
                .
              </p>
            )}
            <p
              className="font-display font-extrabold mb-1"
              style={{ color: 'var(--white)', fontSize: '2rem' }}
            >
              £{price}<span className="font-body text-base font-normal" style={{ color: 'var(--ts)' }}>/month</span>
            </p>
            <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
              {isTrial
                ? null
                : isCancelling || isCancelled
                ? <>Monitoring continues until <span className="font-mono font-medium" style={{ color: 'var(--white)' }}>{formatDate(accessUntil)}</span>.</>
                : currentPeriodEnd
                ? <>Next charge: <span className="font-mono font-medium" style={{ color: 'var(--white)' }}>{formatDate(currentPeriodEnd)}</span></>
                : null}
            </p>
          </div>
        </div>

        {/* Features list */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {features.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div
                className="flex items-center justify-center rounded-full"
                style={{
                  width: '18px',
                  height: '18px',
                  background: 'rgba(74,222,128,0.15)',
                  border: '1px solid rgba(74,222,128,0.3)',
                }}
              >
                <Check size={12} style={{ color: 'var(--ok)' }} />
              </div>
              <span className="font-body text-[0.8rem]" style={{ color: 'var(--tm)' }}>
                {feature}
              </span>
            </div>
          ))}
        </div>

        {/* Payment method — real cards from the Stripe customer */}
        <div
          className="rounded-[6px] p-4 mb-4"
          style={{
            background: 'var(--l2)',
            border: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-[0.7rem] font-medium mb-2" style={{ color: 'var(--ts)' }}>
            PAYMENT METHOD
          </p>
          {paymentMethods.length > 0 ? (
            <div className="flex flex-col gap-2">
              {paymentMethods.map((pm) => (
                <div key={pm.id} className="flex items-center gap-3">
                  <div
                    className="rounded-[4px] px-2 py-1 font-mono text-[0.7rem] font-bold"
                    style={{
                      background: 'var(--l3)',
                      border: '1px solid var(--b2)',
                      color: 'var(--white)',
                    }}
                  >
                    {formatCardBrand(pm.brand)}
                  </div>
                  <span className="font-mono text-[0.85rem]" style={{ color: 'var(--white)' }}>
                    •••• {pm.last4}
                  </span>
                  <span className="font-mono text-[0.78rem]" style={{ color: 'var(--ts)' }}>
                    exp {formatCardExpiry(pm.exp_month, pm.exp_year)}
                  </span>
                  {pm.is_default && (
                    <span
                      className="px-2 py-0.5 rounded-[3px] font-body text-[0.62rem] font-bold uppercase tracking-[0.1em]"
                      style={{
                        background: 'rgba(74,222,128,0.1)',
                        border: '1px solid rgba(74,222,128,0.2)',
                        color: 'var(--ok)',
                      }}
                    >
                      Default
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p style={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.875rem', color: 'var(--ts)' }}>
                No card on file
              </p>
              <Button
                onClick={openPortal}
                disabled={portalLoading}
                className="font-body text-sm px-4 py-2 rounded-[3px] flex items-center gap-2"
                style={{ background: 'var(--a)', color: '#fff', opacity: portalLoading ? 0.6 : 1 }}
              >
                Add payment method
                <ExternalLink size={14} />
              </Button>
            </div>
          )}
          <button
            onClick={openPortal}
            disabled={portalLoading}
            className="mt-3 font-body text-[0.75rem] inline-flex items-center gap-1 transition-colors"
            style={{ background: 'transparent', border: 'none', color: 'var(--a)', cursor: portalLoading ? 'default' : 'pointer', padding: 0 }}
          >
            Update via Stripe Customer Portal
            <ExternalLink size={12} />
          </button>
        </div>
      </div>

      {/* Card 2: Invoice History */}
      <div
        className="rounded-[10px] overflow-hidden mb-4"
        style={{
          background: 'var(--l1)',
          border: '1px solid var(--b1)',
        }}
      >
        <div className="p-6 pb-4">
          <h3 className="font-display text-lg font-bold mb-1" style={{ color: 'var(--white)' }}>
            Invoice History
          </h3>
          <p className="font-body text-[0.75rem]" style={{ color: 'var(--ts)' }}>
            Download past invoices and payment receipts
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead
              style={{
                background: 'rgba(255,255,255,0.015)',
                borderTop: '1px solid var(--b1)',
                borderBottom: '1px solid var(--b1)',
              }}
            >
              <tr>
                {['Invoice', 'Date', 'Amount', 'Status', 'Actions'].map((label) => (
                  <th key={label} className="text-left px-6 py-3">
                    <span className="font-body text-[0.6rem] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                      {label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr style={{ background: 'var(--l3)' }}>
                  <td colSpan={5} className="px-6 py-8 text-center">
                    <span className="font-body text-sm" style={{ color: 'var(--ts)' }}>
                      {invoicesLoaded
                        ? 'No invoices yet — your first invoice will appear after your first payment.'
                        : 'Loading invoices…'}
                    </span>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice, index) => (
                  <tr
                    key={invoice.id}
                    className="transition-colors"
                    style={{
                      background: index % 2 === 0 ? 'var(--l3)' : 'var(--l4)',
                      borderBottom: '1px solid var(--b1)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.025)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = index % 2 === 0 ? 'var(--l3)' : 'var(--l4)';
                    }}
                  >
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm font-medium" style={{ color: 'var(--white)' }}>
                        {invoice.number || invoice.id}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm" style={{ color: 'var(--ts)' }}>
                        {formatDate(invoice.created)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm font-medium" style={{ color: 'var(--white)' }}>
                        £{Number(invoice.amount_paid).toFixed(2)} {invoice.currency}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="px-3 py-1 rounded-[3px] font-body text-[0.7rem] font-medium capitalize"
                        style={{
                          background: 'rgba(74,222,128,0.1)',
                          border: '1px solid rgba(74,222,128,0.2)',
                          color: 'var(--ok)',
                        }}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {invoice.hosted_invoice_url && (
                          <a
                            href={invoice.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[3px] font-body text-[0.72rem] transition-colors"
                            style={{ border: '1px solid var(--b1)', color: 'var(--ts)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--a)'; e.currentTarget.style.color = 'var(--a)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--b1)'; e.currentTarget.style.color = 'var(--ts)'; }}
                          >
                            View
                            <ExternalLink size={13} />
                          </a>
                        )}
                        {invoice.invoice_pdf && (
                          <a
                            href={invoice.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[3px] font-body text-[0.72rem] transition-colors"
                            style={{ border: '1px solid var(--b1)', color: 'var(--ts)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--a)'; e.currentTarget.style.color = 'var(--a)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--b1)'; e.currentTarget.style.color = 'var(--ts)'; }}
                          >
                            <Download size={13} />
                            Download PDF
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Card 3: Danger Zone */}
      <div
        className="rounded-[10px] p-6"
        style={{
          background: 'var(--l1)',
          border: '1px solid rgba(248,113,113,0.3)',
        }}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={20} style={{ color: 'var(--danger)' }} />
              <h3 className="font-display text-base font-bold" style={{ color: 'var(--white)' }}>
                Danger Zone
              </h3>
            </div>
            <p className="font-body text-[0.8rem] mb-1" style={{ color: 'var(--tm)' }}>
              Cancel your Acodia Watch subscription
            </p>
            <p className="font-body text-[0.72rem]" style={{ color: 'var(--te)' }}>
              Your access will continue until the end of the current billing period
            </p>
          </div>
          <Button
            onClick={handleCancelSubscription}
            disabled={isCancelling || isCancelled}
            data-testid="cancel-subscription-button"
            className="font-body text-sm px-4 py-2 rounded-[3px]"
            style={{
              background: (isCancelling || isCancelled) ? 'transparent' : 'var(--danger)',
              color: (isCancelling || isCancelled) ? 'var(--te)' : '#fff',
              border: (isCancelling || isCancelled) ? '1px solid var(--b1)' : 'none',
              opacity: (isCancelling || isCancelled) ? 0.5 : 1,
              cursor: (isCancelling || isCancelled) ? 'not-allowed' : 'pointer',
            }}
          >
            {isCancelled ? 'Subscription Cancelled' : isCancelling ? 'Cancellation Pending' : 'Cancel Subscription'}
          </Button>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(6px)',
          }}
          onClick={handleCancelModal}
        >
          <div
            className="rounded-[10px] p-8"
            style={{
              background: 'var(--l2)',
              border: '1px solid var(--b2)',
              width: '520px',
              maxWidth: '90vw',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="flex items-center justify-center rounded-full"
                style={{
                  width: '48px',
                  height: '48px',
                  background: 'rgba(248,113,113,0.15)',
                  border: '1px solid rgba(248,113,113,0.3)',
                }}
              >
                <AlertCircle size={24} style={{ color: 'var(--danger)' }} />
              </div>
              <h3 className="font-display text-lg font-bold" style={{ color: 'var(--white)' }}>
                Cancel Acodia Watch?
              </h3>
            </div>
            <p className="font-body text-[0.88rem] mb-2" style={{ color: 'var(--tm)' }}>
              Are you sure? Your monitoring will stop at the end of your current period.
            </p>
            <p className="font-body text-[0.82rem] mb-6" style={{ color: 'var(--te)' }}>
              Monitoring continues until{' '}
              <span className="font-mono font-medium" style={{ color: 'var(--white)' }}>
                {formatDate(accessUntil) || 'the end of your current period'}
              </span>.
              {' '}After that, monitoring stops and you&rsquo;ll lose dashboard access.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={handleCancelModal}
                className="font-body text-sm px-4 py-2 rounded-[3px]"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--b1)',
                  color: 'var(--ts)',
                }}
              >
                Keep Subscription
              </Button>
              <Button
                onClick={handleConfirmCancel}
                disabled={cancelling}
                data-testid="confirm-cancel-button"
                className="font-body text-sm px-4 py-2 rounded-[3px]"
                style={{
                  background: 'var(--danger)',
                  color: '#fff',
                  opacity: cancelling ? 0.6 : 1,
                }}
              >
                {cancelling ? 'Cancelling…' : 'Confirm Cancellation'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {showToast && (
        <div
          className="fixed bottom-6 right-6 px-4 py-3 rounded-md shadow-lg animate-fade-in z-50"
          style={{
            background: 'var(--l3)',
            border: '1px solid var(--b1)',
          }}
        >
          <p className="font-body text-[0.78rem]" style={{ color: 'var(--white)' }}>
            {toastMessage}
          </p>
        </div>
      )}
    </div>
  );
}

function WelcomeModal({ domains = [], onVerify, onDismiss }) {
  return (
    <div
      data-testid="dashboard-welcome-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={onDismiss}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg rounded-[10px] p-7"
        style={{ background: 'var(--l1)', border: '1px solid var(--b2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--a)' }}>
          <Shield size={16} />
          <span className="font-body text-xs uppercase tracking-[0.22em]">Acodia Defend</span>
        </div>
        <h2 className="font-display text-2xl font-extrabold tracking-[-0.02em] mb-2" style={{ color: 'var(--white)' }}>
          Welcome to Acodia Watch.
        </h2>
        <p className="font-body text-sm mb-5" style={{ color: 'rgba(240,237,248,0.8)', lineHeight: 1.65 }}>
          Your account is active. Verify your domain to start monitoring.
        </p>

        {domains.length > 0 && (
          <div className="mb-5">
            <p className="font-body text-xs mb-2" style={{ color: 'var(--ts)' }}>
              Domains on your plan:
            </p>
            <div className="flex flex-wrap gap-2" data-testid="welcome-domain-chips">
              {domains.map((d) => (
                <span
                  key={d.id || d.domain}
                  className="font-mono text-xs px-2.5 py-1 rounded-[3px]"
                  style={{
                    background: 'rgba(139,92,246,0.08)',
                    border: '1px solid var(--b2)',
                    color: d.verified ? 'var(--ok)' : 'var(--a-bright)',
                  }}
                >
                  {d.domain}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <Button
            onClick={onDismiss}
            data-testid="welcome-later"
            className="font-body text-sm h-10 px-4 rounded-[3px]"
            style={{ background: 'transparent', border: '1px solid var(--b2)', color: 'var(--ts)' }}
          >
            I will do this later
          </Button>
          <Button
            onClick={onVerify}
            data-testid="welcome-verify"
            className="btn-shimmer font-body text-sm h-10 px-4 rounded-[3px]"
            style={{ background: 'var(--a)', color: 'var(--white)' }}
          >
            Verify My Domains Now
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── 3-step onboarding modal — first-run experience for new users. ── */
function OnboardingModal({ user, authFetch, onComplete }) {
  const [step, setStep] = useState(1);
  const [domain, setDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const firstName = user?.first_name || user?.email?.split('@')[0] || 'there';

  const sanitiseDomain = (raw) => (raw || '')
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0].trim().toLowerCase();
  const isValid = (raw) => /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(sanitiseDomain(raw));

  const handleAddDomain = async () => {
    setError('');
    if (!isValid(domain)) {
      setError('Please enter a valid domain like yourbusiness.co.uk');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/domains/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: sanitiseDomain(domain) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof data?.detail === 'string'
          ? data.detail
          : (data?.detail?.error || 'Could not add domain');
        throw new Error(msg);
      }
      setStep(3);
    } catch (err) {
      setError(err?.message || 'Could not add domain');
    } finally {
      setSubmitting(false);
    }
  };

  const Dots = () => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: '0.75rem' }}>
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          style={{
            width: 7, height: 7, borderRadius: '50%',
            background: n === step ? 'var(--a)' : 'rgba(255,255,255,0.15)',
          }}
        />
      ))}
    </div>
  );

  const overlayStyle = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '1rem',
  };
  const cardStyle = {
    background: 'rgba(10,8,20,0.98)',
    border: '1px solid rgba(139,92,246,0.3)',
    borderRadius: 16, padding: '2rem',
    maxWidth: 500, width: '100%',
    boxShadow: '0 0 60px rgba(139,92,246,0.15)',
    textAlign: 'center',
  };
  const titleStyle = {
    fontFamily: "'Syne', sans-serif", fontWeight: 700,
    fontSize: '1.3rem', color: 'var(--white)',
    marginTop: '1rem',
  };
  const bodyStyle = {
    fontFamily: 'DM Sans, sans-serif', fontSize: '0.92rem',
    color: 'var(--tm)', marginTop: '0.65rem', lineHeight: 1.55,
  };
  const primaryButton = {
    background: 'var(--a)', color: '#fff',
    width: '100%', height: 44, borderRadius: 3,
    fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
    marginTop: '1.25rem',
  };

  return (
    <div data-testid="dashboard-onboarding-modal" style={overlayStyle}>
      <div style={cardStyle}>
        {step === 1 && (
          <>
            <ShieldCheck size={48} style={{ color: 'var(--a)', margin: '0 auto' }} />
            <p style={titleStyle}>Welcome, {firstName}!</p>
            <p style={bodyStyle}>
              Your 7-day free trial has started. Let us get your website protected in 3 simple steps.
            </p>
            <Dots />
            <Button
              type="button"
              data-testid="onboarding-step1-next"
              onClick={() => setStep(2)}
              className="btn-shimmer"
              style={primaryButton}
            >
              <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
                Let&apos;s get started <ArrowRight size={14} />
              </span>
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <Globe size={48} style={{ color: 'var(--a)', margin: '0 auto' }} />
            <p style={titleStyle}>Add your domain</p>
            <p style={bodyStyle}>
              Add the website you want to monitor — we will start scanning it immediately.
            </p>
            <input
              data-testid="onboarding-domain-input"
              placeholder="yourbusiness.co.uk"
              value={domain}
              onChange={(e) => { setDomain(e.target.value); if (error) setError(''); }}
              style={{
                width: '100%', height: 44, marginTop: '1rem',
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid var(--b1)', borderRadius: 3,
                padding: '0 0.75rem', color: 'var(--white)',
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem',
                outline: 'none',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--a)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--b1)'}
            />
            {error && (
              <p style={{
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem',
                color: 'var(--danger)', marginTop: '0.4rem', textAlign: 'left',
              }}>
                {error}
              </p>
            )}
            <Dots />
            <Button
              type="button"
              data-testid="onboarding-step2-add"
              onClick={handleAddDomain}
              disabled={submitting}
              className="btn-shimmer"
              style={{ ...primaryButton, opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? (
                <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
                  <Loader2 size={14} style={{ animation: 'scannerSpin 1s linear infinite' }} />
                  Adding...
                </span>
              ) : (
                <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
                  Add Domain <ArrowRight size={14} />
                </span>
              )}
            </Button>
            <button
              type="button"
              data-testid="onboarding-step2-skip"
              onClick={() => setStep(3)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem',
                color: 'var(--ts)', marginTop: '0.75rem',
              }}
            >
              I&apos;ll do this later
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(74,222,128,0.12)',
              border: '1px solid rgba(74,222,128,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto',
            }}>
              <Check size={28} style={{ color: 'var(--ok)' }} />
            </div>
            <p style={titleStyle}>You&apos;re all set</p>
            <p style={bodyStyle}>
              Acodia Watch is now monitoring your website. Your first security scan will run
              shortly. Check back in a few minutes for your results.
            </p>
            <Dots />
            <Button
              type="button"
              data-testid="onboarding-step3-finish"
              onClick={onComplete}
              className="btn-shimmer"
              style={primaryButton}
            >
              <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
                Go to Dashboard <ArrowRight size={14} />
              </span>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
